import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import '../styles/Admin.css';
import WorldBuilder from './WorldBuilder';

const API = process.env.REACT_APP_SERVER_URL || '';

// ────────────────────────────────────────────────────────────
//  LoreMarkdown — minimal in-house markdown renderer.
//
//  Handles the syntax actually used in docs/lore/*.md:
//  - ATX headings (# .. ######)
//  - paragraphs
//  - unordered lists (- )
//  - ordered lists (1. )
//  - GFM-style tables (| a | b | with --- separator)
//  - **bold**, *italic*, `inline code`
//  - horizontal rule (---)
//  - links [text](href)
//
//  Deliberately avoids a markdown dependency. Output escapes user-
//  uncontrolled HTML by virtue of using React text nodes everywhere
//  except where we explicitly construct elements.
// ────────────────────────────────────────────────────────────

function renderInline(text, keyPrefix = 'i') {
  // Handle inline tokens in priority order:
  //   `code`  **bold**  *italic*  [text](url)
  // We do a single linear scan with a regex alternation.
  const parts = [];
  const regex = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(\[[^\]]+\]\([^)]+\))/g;
  let lastIdx = 0;
  let match;
  let n = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.slice(lastIdx, match.index));
    }
    const tok = match[0];
    const k = `${keyPrefix}-${n++}`;
    if (tok.startsWith('`')) {
      parts.push(<code key={k} style={{ background: '#222', padding: '1px 5px', borderRadius: 3, fontSize: '0.92em' }}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      parts.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*')) {
      parts.push(<em key={k}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith('[')) {
      const m = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        // Internal cross-doc links resolve relative to docs/lore/ — render
        // as plain text-with-emphasis since the AdminPanel cross-nav
        // would require URL-rewriting we don't need here.
        parts.push(<span key={k} style={{ color: '#7cb7ff', textDecoration: 'underline dotted' }}>{m[1]}</span>);
      } else {
        parts.push(tok);
      }
    } else {
      parts.push(tok);
    }
    lastIdx = match.index + tok.length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

function LoreMarkdown({ source, highlight }) {
  if (!source) return null;
  const lines = source.split(/\r?\n/);
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — skip
    if (line.trim() === '') { i++; continue; }

    // Horizontal rule
    if (/^-{3,}\s*$/.test(line)) {
      blocks.push({ type: 'hr', key: `hr-${i}` });
      i++;
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (h) {
      blocks.push({ type: 'h', level: h[1].length, text: h[2], key: `h-${i}` });
      i++;
      continue;
    }

    // Table — header row | --- row | body rows
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const rows = [];
      const splitRow = (l) => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(s => s.trim());
      rows.push(splitRow(line));            // header
      i += 2;                                // skip separator
      while (i < lines.length && lines[i].includes('|')) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', rows, key: `tbl-${i}` });
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items, key: `ul-${i}` });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items, key: `ol-${i}` });
      continue;
    }

    // Paragraph — gather until a blank or block-starter line
    const para = [line];
    i++;
    while (i < lines.length
        && lines[i].trim() !== ''
        && !/^#{1,6}\s+/.test(lines[i])
        && !/^-{3,}\s*$/.test(lines[i])
        && !/^\s*[-*]\s+/.test(lines[i])
        && !/^\s*\d+\.\s+/.test(lines[i])
        && !lines[i].includes('|')) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', text: para.join(' '), key: `p-${i}` });
  }

  // Optional search highlighting — wrap matches in a <mark>.
  const wrap = (text, k) => {
    if (!highlight) return renderInline(text, k);
    // Highlight is applied AFTER inline parsing so emphasis still works.
    // We split on the highlight term case-insensitively and interleave.
    const parts = renderInline(text, k);
    const flat = [];
    parts.forEach((p, idx) => {
      if (typeof p !== 'string') { flat.push(p); return; }
      const re = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
      const split = p.split(re);
      split.forEach((s, j) => {
        if (re.test(s) && s.toLowerCase() === highlight.toLowerCase()) {
          flat.push(<mark key={`${k}-hl-${idx}-${j}`} style={{ background: '#5a4500', color: '#ffe9a8', padding: '0 2px' }}>{s}</mark>);
        } else if (s) {
          flat.push(s);
        }
      });
    });
    return flat;
  };

  const headingStyle = (level) => {
    const sizes = [0, 28, 22, 18, 16, 14, 13];
    const margins = [0, '24px 0 12px', '20px 0 10px', '18px 0 8px', '16px 0 6px', '14px 0 4px', '12px 0 4px'];
    return {
      fontSize: sizes[level],
      margin: margins[level],
      color: level <= 2 ? '#ffd9a8' : '#e0c890',
      borderBottom: level === 1 ? '1px solid #3a2f1f' : level === 2 ? '1px solid #2a2418' : 'none',
      paddingBottom: level <= 2 ? 4 : 0,
    };
  };

  return (
    <div style={{
      color: '#e0e0e0',
      fontSize: 14,
      lineHeight: 1.6,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      {blocks.map(b => {
        if (b.type === 'hr') return <hr key={b.key} style={{ border: 'none', borderTop: '1px solid #2a2a2a', margin: '20px 0' }} />;
        if (b.type === 'h') {
          const Tag = `h${b.level}`;
          return React.createElement(Tag, { key: b.key, style: headingStyle(b.level) }, wrap(b.text, b.key));
        }
        if (b.type === 'p') {
          return <p key={b.key} style={{ margin: '8px 0' }}>{wrap(b.text, b.key)}</p>;
        }
        if (b.type === 'ul') {
          return (
            <ul key={b.key} style={{ margin: '8px 0', paddingLeft: 24 }}>
              {b.items.map((it, idx) => <li key={`${b.key}-${idx}`} style={{ margin: '3px 0' }}>{wrap(it, `${b.key}-${idx}`)}</li>)}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={b.key} style={{ margin: '8px 0', paddingLeft: 24 }}>
              {b.items.map((it, idx) => <li key={`${b.key}-${idx}`} style={{ margin: '3px 0' }}>{wrap(it, `${b.key}-${idx}`)}</li>)}
            </ol>
          );
        }
        if (b.type === 'table') {
          const [head, ...body] = b.rows;
          return (
            <div key={b.key} style={{ overflowX: 'auto', margin: '12px 0' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    {head.map((c, j) => (
                      <th key={j} style={{
                        textAlign: 'left',
                        padding: '6px 10px',
                        background: '#222',
                        borderBottom: '1px solid #444',
                        color: '#ffd9a8',
                      }}>{wrap(c, `${b.key}-h-${j}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.map((r, i2) => (
                    <tr key={i2} style={{ background: i2 % 2 === 0 ? '#1c1c1c' : '#181818' }}>
                      {r.map((c, j) => (
                        <td key={j} style={{
                          padding: '6px 10px',
                          borderBottom: '1px solid #2a2a2a',
                          verticalAlign: 'top',
                        }}>{wrap(c, `${b.key}-${i2}-${j}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('last_action');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [monsters, setMonsters] = useState([]);
  const [items, setItems] = useState({});
  const [actionLog, setActionLog] = useState([]);
  const [monsterSearch, setMonsterSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSortField, setAccountSortField] = useState('last_login');
  const [accountSortOrder, setAccountSortOrder] = useState('DESC');
  const [modPlayers, setModPlayers] = useState([]);
  const [modLoading, setModLoading] = useState(false);
  const [modLog, setModLog] = useState([]);
  const [modLogTotal, setModLogTotal] = useState(0);
  // Phase 4 — audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilterCategory, setAuditFilterCategory] = useState('all');
  const [auditFilterEventType, setAuditFilterEventType] = useState('');
  const [auditFilterSearch, setAuditFilterSearch] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const AUDIT_PAGE_SIZE = 100;
  const [directAction, setDirectAction] = useState('mute');
  const [directActionName, setDirectActionName] = useState('');
  const [directActionDuration, setDirectActionDuration] = useState('1440');
  const [directActionReason, setDirectActionReason] = useState('');

  // Crafting admin state
  const [craftingAnalytics, setCraftingAnalytics] = useState(null);
  const [craftingPlayerId, setCraftingPlayerId] = useState('');
  const [craftingPlayerData, setCraftingPlayerData] = useState(null);
  const [craftingDiscipline, setCraftingDiscipline] = useState('smithing');
  const [craftingXpAmount, setCraftingXpAmount] = useState('');
  const [craftingLevelTarget, setCraftingLevelTarget] = useState('');
  const [craftingStatusMsg, setCraftingStatusMsg] = useState(null);

  // Quest guide admin state
  const [questGuide, setQuestGuide] = useState(null);
  const [questGuideLoading, setQuestGuideLoading] = useState(false);
  const [selectedQuestPath, setSelectedQuestPath] = useState(null);
  const [questGuideCategory, setQuestGuideCategory] = useState('all');
  const [questGuideSearch, setQuestGuideSearch] = useState('');
  const [questGuideView, setQuestGuideView] = useState('list'); // 'list' | 'chains' | 'graph' | 'pacing'
  const [dagShowLockouts, setDagShowLockouts] = useState(true);
  const [dagShowCrossChain, setDagShowCrossChain] = useState(true);
  const [dagShowAdvances, setDagShowAdvances] = useState(true);
  const [dagZoom, setDagZoom] = useState(1);
  const [viewportW, setViewportW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1400);

  // Lore admin state
  const [loreDocs, setLoreDocs] = useState(null);
  const [loreLoading, setLoreLoading] = useState(false);
  const [selectedLoreId, setSelectedLoreId] = useState(null);
  const [loreSearch, setLoreSearch] = useState('');

  // Bug reports admin state
  const [bugReports, setBugReports] = useState([]);
  const [bugTotal, setBugTotal] = useState(0);
  const [bugFilter, setBugFilter] = useState('unresolved');
  const [bugPage, setBugPage] = useState(0);
  const [selectedBug, setSelectedBug] = useState(null);
  const [bugAdminNotes, setBugAdminNotes] = useState('');
  const [bugExportText, setBugExportText] = useState(null);

  // Player reports admin state
  const [playerReports, setPlayerReports] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportFilter, setReportFilter] = useState('open');
  const [reportPage, setReportPage] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportChatContext, setReportChatContext] = useState([]);
  const [reportAdminNotes, setReportAdminNotes] = useState('');
  const [reportAction, setReportAction] = useState('none');
  const [reportSummary, setReportSummary] = useState(null);

  // Quick action state
  const [giveGoldAmount, setGiveGoldAmount] = useState('');
  const [giveXpAmount, setGiveXpAmount] = useState('');
  const [teleportArea, setTeleportArea] = useState('');
  const [teleportX, setTeleportX] = useState('');
  const [teleportY, setTeleportY] = useState('');
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [giveCraftXpDiscipline, setGiveCraftXpDiscipline] = useState('smithing');
  const [giveCraftXpAmount, setGiveCraftXpAmount] = useState('');
  const [cleanupDays, setCleanupDays] = useState('60');
  const [confirmAction, setConfirmAction] = useState(null);
  const [playerStatusMsg, setPlayerStatusMsg] = useState(null); // {text, type: 'success'|'error'}

  // Monster editor state
  const [selectedMonster, setSelectedMonster] = useState(null);
  const [editMonster, setEditMonster] = useState(null);
  const [monsterMeta, setMonsterMeta] = useState(null); // { abilities, elementTypes, monsterTypes }
  const [monsterSaving, setMonsterSaving] = useState(false);
  const [monsterStatusMsg, setMonsterStatusMsg] = useState(null);

  // Broadcast state
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [currentBroadcast, setCurrentBroadcast] = useState(null);
  const [broadcastStatus, setBroadcastStatus] = useState(null); // {text, type}

  const logAction = (msg) => {
    setActionLog(prev => [{ time: new Date().toLocaleTimeString(), msg }, ...prev.slice(0, 49)]);
  };

  const flashPlayerStatus = (text, type = 'success') => {
    setPlayerStatusMsg({ text, type });
    setTimeout(() => setPlayerStatusMsg(null), type === 'error' ? 10000 : 5000);
  };

  // Fetch dashboard stats 
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch players 
  const fetchPlayers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/players`, {
        params: { search: searchTerm, sort: sortField, order: sortOrder, limit: 50, offset: page * 50 }
      });
      setPlayers(res.data.players);
      setTotalPlayers(res.data.total);
    } catch (err) {
      console.error('Failed to fetch players:', err);
    }
  }, [searchTerm, sortField, sortOrder, page]);

  // Fetch monsters 
  const fetchMonsters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/monsters`);
      setMonsters(res.data);
    } catch (err) {
      console.error('Failed to fetch monsters:', err);
    }
  }, []);

  // Fetch monster editor metadata (abilities list, element types)
  const fetchMonsterMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/monsters/meta`);
      setMonsterMeta(res.data);
    } catch (err) {
      console.error('Failed to fetch monster meta:', err);
    }
  }, []);

  // Fetch items 
  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/items`);
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  }, []);

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/accounts`, {
        params: { search: accountSearch || undefined }
      });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  }, [accountSearch]);

  // Fetch moderation data
  const fetchModeration = useCallback(async () => {
    setModLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/moderation`);
      setModPlayers(res.data.players || []);
    } catch (err) {
      console.error('Failed to fetch moderation data:', err);
    }
    setModLoading(false);
  }, []);

  // Fetch bug reports
  const fetchBugReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/bugs/list`, {
        params: { filter: bugFilter, limit: 50, offset: bugPage * 50 }
      });
      setBugReports(res.data.reports || []);
      setBugTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch bug reports:', err);
    }
  }, [bugFilter, bugPage]);

  // Fetch player reports
  const fetchPlayerReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reports`, {
        params: { status: reportFilter, limit: 50, offset: reportPage * 50 }
      });
      setPlayerReports(res.data.reports || []);
      setReportTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch player reports:', err);
    }
    try {
      const sum = await axios.get(`${API}/api/reports/stats/summary`);
      setReportSummary(sum.data.summary || null);
    } catch { /* noop */ }
  }, [reportFilter, reportPage]);

  const openReport = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/api/reports/${id}`);
      setSelectedReport(res.data.report);
      setReportChatContext(res.data.chatContext || []);
      setReportAdminNotes(res.data.report?.admin_notes || '');
      setReportAction(res.data.report?.action_taken || 'none');
    } catch (err) {
      console.error('Failed to open report:', err);
    }
  }, []);

  const patchReport = useCallback(async (id, payload, closeAfter = false) => {
    try {
      const res = await axios.patch(`${API}/api/reports/${id}`, payload);
      setSelectedReport(res.data.report);
      setReportAdminNotes(res.data.report?.admin_notes || '');
      setReportAction(res.data.report?.action_taken || 'none');
      fetchPlayerReports();
      if (closeAfter) setSelectedReport(null);
    } catch (err) {
      alert('Failed to update report: ' + (err.response?.data?.error || err.message));
    }
  }, [fetchPlayerReports]);

  // Fetch current broadcast
  const fetchBroadcast = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/broadcast/current`);
      setCurrentBroadcast(res.data.broadcast || null);
    } catch (err) {
      console.error('Failed to fetch broadcast:', err);
    }
  }, []);

  // Moderation audit log
  // NOTE: fetchModerationLog / fetchAuditLog / fetchQuestGuide are declared here
  // (before the tab-switch useEffect) instead of further down the file to keep
  // them out of the temporal-dead-zone when the effect first runs. Minified
  // builds otherwise throw "Cannot access 'ts' before initialization".
  const fetchModerationLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/moderation/log`, {
        params: { limit: 50, offset: 0 }
      });
      setModLog(res.data.entries || []);
      setModLogTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch moderation log:', err);
    }
  }, []);

  // Phase 4 — system-wide audit log
  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = {
        limit: AUDIT_PAGE_SIZE,
        offset: auditPage * AUDIT_PAGE_SIZE,
      };
      if (auditFilterCategory && auditFilterCategory !== 'all') params.category = auditFilterCategory;
      if (auditFilterEventType.trim()) params.eventType = auditFilterEventType.trim();
      if (auditFilterSearch.trim()) params.search = auditFilterSearch.trim();
      const res = await axios.get(`${API}/api/admin/audit/log`, { params });
      setAuditLog(res.data.entries || []);
      setAuditTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      setAuditLog([]);
      setAuditTotal(0);
    } finally {
      setAuditLoading(false);
    }
  }, [auditPage, auditFilterCategory, auditFilterEventType, auditFilterSearch]);

  // Quest guide
  const fetchQuestGuide = useCallback(async () => {
    setQuestGuideLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/quest-guide`);
      setQuestGuide(res.data || null);
    } catch (err) {
      console.error('Failed to fetch quest guide:', err);
    } finally {
      setQuestGuideLoading(false);
    }
  }, []);

  // Lore
  const fetchLore = useCallback(async () => {
    setLoreLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/lore`);
      setLoreDocs(res.data || null);
      // Default-select the README so something is always visible.
      if (res.data && Array.isArray(res.data.docs) && res.data.docs.length && !selectedLoreId) {
        setSelectedLoreId(res.data.docs[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch lore:', err);
    } finally {
      setLoreLoading(false);
    }
  }, [selectedLoreId]);

  useEffect(() => {
    if (activeTab === 'dashboard') { fetchStats(); fetchBroadcast(); }
    if (activeTab === 'characters') fetchPlayers();
    if (activeTab === 'monsters') { fetchMonsters(); if (!monsterMeta) fetchMonsterMeta(); }
    if (activeTab === 'items') fetchItems();
    if (activeTab === 'accounts') fetchAccounts();
    if (activeTab === 'moderation') { fetchModeration(); fetchModerationLog(); }
    if (activeTab === 'bugs') fetchBugReports();
    if (activeTab === 'reports') fetchPlayerReports();
    if (activeTab === 'audit') fetchAuditLog();
    if (activeTab === 'questguide' && !questGuide) fetchQuestGuide();
    if (activeTab === 'lore' && !loreDocs) fetchLore();
  }, [activeTab, fetchStats, fetchPlayers, fetchMonsters, fetchMonsterMeta, monsterMeta, fetchItems, fetchAccounts, fetchModeration, fetchBugReports, fetchBroadcast, fetchPlayerReports, fetchModerationLog, fetchAuditLog, fetchQuestGuide, questGuide, fetchLore, loreDocs]);

  // Track viewport width so the Quest Guide Graph toggle can hide on narrow
  // screens (DAG view needs ≥1100px for the 3-column layout to fit cleanly).
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-refresh dashboard stats every 15s — pauses when browser tab is hidden
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    let interval;
    const startPolling = () => {
      interval = setInterval(() => { fetchStats(); }, 15000);
    };
    const handleVisibility = () => {
      if (document.hidden) { clearInterval(interval); }
      else { fetchStats(); startPolling(); }
    };
    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [activeTab, fetchStats]);

  // Broadcast actions
  const handleSendBroadcast = async () => {
    if (!broadcastMessage.trim()) return;
    try {
      const res = await axios.post(`${API}/api/admin/broadcast`, { message: broadcastMessage.trim() });
      logAction(res.data.message);
      setBroadcastStatus({ text: res.data.message, type: 'success' });
      setBroadcastMessage('');
      fetchBroadcast();
      setTimeout(() => setBroadcastStatus(null), 5000);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      logAction(`Broadcast error: ${errMsg}`);
      setBroadcastStatus({ text: errMsg, type: 'error' });
      setTimeout(() => setBroadcastStatus(null), 8000);
    }
  };

  const handleClearBroadcast = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/broadcast/clear`);
      logAction(res.data.message);
      setBroadcastStatus({ text: 'Broadcast cleared', type: 'success' });
      setCurrentBroadcast(null);
      setTimeout(() => setBroadcastStatus(null), 5000);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      logAction(`Clear broadcast error: ${errMsg}`);
      setBroadcastStatus({ text: errMsg, type: 'error' });
      setTimeout(() => setBroadcastStatus(null), 8000);
    }
  };

  // Player actions 
  const handlePlayerAction = async (action, playerId, body = {}) => {
    try {
      let res;
      switch (action) {
        case 'heal':
          res = await axios.post(`${API}/api/admin/players/${playerId}/heal`);
          break;
        case 'give-gold':
          res = await axios.post(`${API}/api/admin/players/${playerId}/give-gold`, body);
          break;
        case 'give-xp':
          res = await axios.post(`${API}/api/admin/players/${playerId}/give-xp`, body);
          break;
        case 'give-item':
          res = await axios.post(`${API}/api/admin/players/${playerId}/give-item`, body);
          break;
        case 'give-crafting-xp':
          res = await axios.post(`${API}/api/admin/players/${playerId}/give-crafting-xp`, body);
          break;
        case 'teleport':
          res = await axios.post(`${API}/api/admin/players/${playerId}/teleport`, body);
          break;
        case 'reset':
          res = await axios.post(`${API}/api/admin/players/${playerId}/reset`);
          break;
        case 'edit':
          res = await axios.put(`${API}/api/admin/players/${playerId}`, body);
          break;
        case 'delete':
          res = await axios.delete(`${API}/api/admin/players/${playerId}`);
          break;
        default:
          return;
      }
      logAction(`${res.data.message}`);
      flashPlayerStatus(res.data.message, 'success');
      // Refresh data
      if (action === 'delete') {
        setSelectedPlayer(null);
        fetchPlayers();
      } else {
        fetchPlayers();
        if (selectedPlayer) {
          const detail = await axios.get(`${API}/api/admin/players/${playerId}`);
          setSelectedPlayer(detail.data);
        }
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      logAction(`Error: ${errMsg}`);
      flashPlayerStatus(errMsg, 'error');
    }
  };

  const handleCleanup = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/cleanup`, { days: parseInt(cleanupDays) });
      logAction(`${res.data.message}`);
      fetchPlayers();
      fetchStats();
    } catch (err) {
      logAction(`Cleanup error: ${err.response?.data?.error || err.message}`);
    }
  };

  const selectPlayer = async (player) => {
    try {
      const res = await axios.get(`${API}/api/admin/players/${player.id}`);
      setSelectedPlayer(res.data);
    } catch (err) {
      logAction(`Failed to load character details`);
    }
  };

  const timeSince = (dateStr) => {
    if (!dateStr) return 'Never';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Helper: toggle sort on a column (for server-sorted Characters table)
  const handlePlayerSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder('DESC');
    }
    setPage(0);
  };

  // Helper: toggle sort on a column (for client-sorted Accounts table)
  const handleAccountSort = (field) => {
    if (accountSortField === field) {
      setAccountSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setAccountSortField(field);
      setAccountSortOrder('DESC');
    }
  };

  // Render a sortable <th> header
  const SortTh = ({ field, label, currentSort, currentOrder, onSort }) => (
    <th className="admin-sortable-th" onClick={() => onSort(field)}>
      {label}
      {currentSort === field
        ? <span className="admin-sort-arrow">{currentOrder === 'ASC' ? ' ▲' : ' ▼'}</span>
        : <span className="admin-sort-arrow admin-sort-arrow-idle"> ⇅</span>}
    </th>
  );

  // Client-side sorted accounts
  const sortedAccounts = [...accounts].sort((a, b) => {
    let aVal = a[accountSortField];
    let bVal = b[accountSortField];
    // Null/undefined goes last
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    // Numeric fields
    if (accountSortField === 'character_count') {
      aVal = Number(aVal); bVal = Number(bVal);
    }
    // Date fields
    if (accountSortField === 'last_login' || accountSortField === 'created_at') {
      aVal = new Date(aVal).getTime(); bVal = new Date(bVal).getTime();
    }
    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      return accountSortOrder === 'ASC' ? cmp : -cmp;
    }
    // Numeric comparison
    return accountSortOrder === 'ASC' ? aVal - bVal : bVal - aVal;
  });

  // Confirm dialog 
  const ConfirmDialog = () => {
    if (!confirmAction) return null;
    return (
      <div className="admin-modal-overlay">
        <div className="admin-modal">
          <h3>Confirm Action</h3>
          <p>{confirmAction.message}</p>
          <div className="admin-modal-buttons">
            <button className="admin-btn admin-btn-danger" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>
              Yes, Do It
            </button>
            <button className="admin-btn" onClick={() => setConfirmAction(null)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // DASHBOARD TAB
  // ============================================================
  const renderDashboard = () => {
    if (!stats) return <div className="admin-loading">Loading stats...</div>;

    return (
      <div className="admin-dashboard">
        {/* Broadcast panel */}
        <div className="admin-panel admin-broadcast-panel">
          <h3>📢 Global Broadcast</h3>
          <p className="admin-muted">Send a message banner to all players. It stays visible until dismissed or cleared.</p>
          {currentBroadcast && (
            <div className="admin-broadcast-current">
              <div className="admin-broadcast-preview">
                <span className="admin-broadcast-preview-label">Currently Active:</span>
                <span className="admin-broadcast-preview-msg">{currentBroadcast.message}</span>
                <span className="admin-broadcast-preview-meta">by {currentBroadcast.sentBy} — {timeSince(currentBroadcast.sentAt)}</span>
              </div>
              <button className="admin-btn admin-btn-warning" onClick={handleClearBroadcast}>
                ✕ Clear Broadcast
              </button>
            </div>
          )}
          <div className="admin-broadcast-form">
            <textarea
              className="admin-input admin-broadcast-input"
              placeholder="Type your broadcast message here... (max 500 characters)"
              value={broadcastMessage}
              onChange={e => setBroadcastMessage(e.target.value)}
              maxLength={500}
              rows={2}
            />
            <div className="admin-broadcast-actions">
              <span className="admin-muted">{broadcastMessage.length}/500</span>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSendBroadcast}
                disabled={!broadcastMessage.trim()}
              >
                📢 Send Broadcast
              </button>
            </div>
          </div>
          {broadcastStatus && (
            <div className={`admin-broadcast-status ${broadcastStatus.type}`}>{broadcastStatus.text}</div>
          )}
        </div>

        {/* Summary cards */}
        <div className="admin-stat-cards">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalUsers}</div>
            <div className="admin-stat-label">Total Accounts</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalCharacters}</div>
            <div className="admin-stat-label">Total Characters</div>
          </div>
          <div className="admin-stat-card highlight">
            <div className="admin-stat-value">{stats.active24h}</div>
            <div className="admin-stat-label">Active (24h)</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.active7d}</div>
            <div className="admin-stat-label">Active (7d)</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalAdmins}</div>
            <div className="admin-stat-label">Admins</div>
          </div>
        </div>

        <div className="admin-dashboard-grid">
          {/* Class distribution */}
          <div className="admin-panel">
            <h3>Class Distribution</h3>
            <div className="admin-distribution">
              {stats.classDistribution.map(c => (
                <div key={c.class} className="admin-dist-row">
                  <span className="admin-dist-label">{c.class}</span>
                  <div className="admin-dist-bar">
                    <div className="admin-dist-fill" style={{ width: `${(c.count / stats.totalCharacters) * 100}%` }} />
                  </div>
                  <span className="admin-dist-count">{c.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Race distribution */}
          <div className="admin-panel">
            <h3>Race Distribution</h3>
            <div className="admin-distribution">
              {stats.raceDistribution.map(r => (
                <div key={r.race} className="admin-dist-row">
                  <span className="admin-dist-label">{r.race}</span>
                  <div className="admin-dist-bar">
                    <div className="admin-dist-fill race" style={{ width: `${(r.count / stats.totalCharacters) * 100}%` }} />
                  </div>
                  <span className="admin-dist-count">{r.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Level distribution */}
          <div className="admin-panel">
            <h3>Level Distribution</h3>
            <div className="admin-distribution">
              {stats.levelDistribution.map(l => (
                <div key={l.bracket} className="admin-dist-row">
                  <span className="admin-dist-label">Lv {l.bracket}</span>
                  <div className="admin-dist-bar">
                    <div className="admin-dist-fill level" style={{ width: `${(l.count / stats.totalCharacters) * 100}%` }} />
                  </div>
                  <span className="admin-dist-count">{l.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Online characters */}
          <div className="admin-panel">
            <h3>
              <span className="admin-live-dot" title="Auto-refreshes every 15s" />
              Online Characters ({stats.onlinePlayers.length})
            </h3>
            {stats.onlinePlayers.length === 0 ? (
              <p className="admin-muted">No characters online</p>
            ) : (
              <div className="admin-online-list">
                {stats.onlinePlayers.map((p, i) => (
                  <div key={i} className="admin-online-player">
                    <span className="admin-online-name">{p.name}</span>
                    <span className="admin-online-info">Lv{p.level} {p.class}</span>
                    <span className="admin-online-area">{p.current_area}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="admin-panel full-width">
          <h3>Top Players</h3>
          <table className="admin-table">
            <thead>
              <tr><th>#</th><th>Name</th><th>Class</th><th>Race</th><th>Level</th><th>XP</th><th>Gold</th></tr>
            </thead>
            <tbody>
              {stats.topPlayers.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{p.name}</td>
                  <td>{p.class}</td>
                  <td>{p.race}</td>
                  <td>{p.level}</td>
                  <td>{p.experience?.toLocaleString()}</td>
                  <td>{p.gold?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================================
  // PLAYERS TAB
  // ============================================================
  const renderPlayers = () => (
    <div className="admin-players">
      <div className="admin-players-list">
        {/* Search & controls */}
        <div className="admin-search-bar">
          <input
            type="text"
            placeholder="Search by name, username, or email..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
            className="admin-input admin-search-input"
          />
          <select value={sortField} onChange={e => setSortField(e.target.value)} className="admin-select">
            <option value="last_action">Last Active</option>
            <option value="name">Name</option>
            <option value="level">Level</option>
            <option value="experience">XP</option>
            <option value="gold">Gold</option>
            <option value="class">Class</option>
          </select>
          <button className="admin-btn admin-btn-sm" onClick={() => setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC')} title={sortOrder === 'ASC' ? 'Sort descending' : 'Sort ascending'}>
            {sortOrder === 'ASC' ? '▲' : '▼'}
          </button>
          <button className="admin-btn admin-btn-sm" onClick={fetchPlayers} title="Refresh list">🔄</button>
        </div>

        {/* Player list */}
        <div className="admin-player-table-wrapper">
          <table className="admin-table admin-player-table">
            <thead>
              <tr>
                <SortTh field="name" label="Name" currentSort={sortField} currentOrder={sortOrder} onSort={handlePlayerSort} />
                <th>User</th>
                <SortTh field="class" label="Class" currentSort={sortField} currentOrder={sortOrder} onSort={handlePlayerSort} />
                <SortTh field="level" label="Lv" currentSort={sortField} currentOrder={sortOrder} onSort={handlePlayerSort} />
                <SortTh field="current_area" label="Area" currentSort={sortField} currentOrder={sortOrder} onSort={handlePlayerSort} />
                <SortTh field="last_action" label="Last Active" currentSort={sortField} currentOrder={sortOrder} onSort={handlePlayerSort} />
              </tr>
            </thead>
            <tbody>
              {players.map(p => (
                <tr
                  key={p.id}
                  className={selectedPlayer?.id === p.id ? 'selected' : ''}
                  onClick={() => selectPlayer(p)}
                >
                  <td>{p.name}</td>
                  <td>{p.username}</td>
                  <td>{p.class}</td>
                  <td>{p.level}</td>
                  <td className="admin-area-col">{p.current_area?.replace('.dat', '')}</td>
                  <td>{timeSince(p.last_action)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="admin-pagination">
          <button className="admin-btn admin-btn-sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
             Prev
          </button>
          <span className="admin-page-info">
            {page * 50 + 1}–{Math.min((page + 1) * 50, totalPlayers)} of {totalPlayers}
          </span>
          <button className="admin-btn admin-btn-sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= totalPlayers}>
            Next </button>
        </div>
      </div>

      {/* Player detail panel */}
      <div className="admin-player-detail">
        {selectedPlayer ? renderPlayerDetail() : (
          <div className="admin-no-selection">
            <p>Select a character to view details</p>
          </div>
        )}
      </div>
    </div>
  );

  // Player Detail 
  const renderPlayerDetail = () => {
    const p = selectedPlayer;
    if (!p) return null;

    let inventory = [];
    try { inventory = typeof p.inventory === 'string' ? JSON.parse(p.inventory) : (p.inventory || []); } catch(e) {}
    let seeds = {};
    try { seeds = typeof p.seeds === 'string' ? JSON.parse(p.seeds) : (p.seeds || {}); } catch(e) {}
    let equipment = {};
    try { equipment = typeof p.equipment === 'string' ? JSON.parse(p.equipment) : (p.equipment || {}); } catch(e) {}
    let petData = null;
    try { petData = typeof p.pet_data === 'string' ? JSON.parse(p.pet_data) : p.pet_data; } catch(e) {}
    let crafting = {};
    try { crafting = typeof p.crafting === 'string' ? JSON.parse(p.crafting) : (p.crafting || {}); } catch(e) {}
    let knownRecipes = [];
    try { knownRecipes = typeof p.known_recipes === 'string' ? JSON.parse(p.known_recipes) : (p.known_recipes || []); } catch(e) {}
    let deckCards = [];
    try { deckCards = typeof p.deck_cards === 'string' ? JSON.parse(p.deck_cards) : (p.deck_cards || []); } catch(e) {}
    const deckCardDetails = Array.isArray(p.deck_card_details) ? p.deck_card_details : [];

    return (
      <div className="admin-detail-content">
        <div className="admin-detail-header">
          <h3>{p.name}</h3>
          <span className="admin-detail-subtitle">
            {p.username} — Lv{p.level} {p.race} {p.class}{p.advanced_class ? ` (${p.advanced_class})` : ''}
          </span>
        </div>

        {/* Action status message */}
        {playerStatusMsg && (
          <div className={`admin-player-status ${playerStatusMsg.type}`}>
            {playerStatusMsg.type === 'success' ? '' : ''} {playerStatusMsg.text}
          </div>
        )}

        {/* Stats grid */}
        <div className="admin-detail-stats">
          <div className="admin-detail-stat">
            <label>HP</label>
            <span>{(p.max_hp - (p.damage || 0))}/{p.max_hp}</span>
          </div>
          <div className="admin-detail-stat">
            <label>MP</label>
            <span>{(p.max_mp - (p.used_mp || 0))}/{p.max_mp}</span>
          </div>
          <div className="admin-detail-stat">
            <label>XP</label>
            <span>{p.experience?.toLocaleString()}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Gold</label>
            <span>{p.gold?.toLocaleString()}</span>
          </div>
          <div className="admin-detail-stat">
            <label>ATT</label>
            <span>{p.att}</span>
          </div>
          <div className="admin-detail-stat">
            <label>DEF</label>
            <span>{p.def}</span>
          </div>
          <div className="admin-detail-stat">
            <label>STR</label>
            <span>{p.strength}</span>
          </div>
          <div className="admin-detail-stat">
            <label>AGL</label>
            <span>{p.agility}</span>
          </div>
          <div className="admin-detail-stat">
            <label>VIT</label>
            <span>{p.vitality}</span>
          </div>
          <div className="admin-detail-stat">
            <label>MEN</label>
            <span>{p.mentality}</span>
          </div>
          <div className="admin-detail-stat">
            <label>CHA</label>
            <span>{p.charisma}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Area</label>
            <span>{p.current_area} ({p.x_position},{p.y_position})</span>
          </div>
          <div className="admin-detail-stat">
            <label>Battle</label>
            <span>{p.battle_state}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Created</label>
            <span>{new Date(p.created_at).toLocaleDateString()}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Last Active</label>
            <span>{timeSince(p.last_action)}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Email</label>
            <span>{p.email}</span>
          </div>
        </div>

        {/* Equipment */}
        {Object.keys(equipment).length > 0 && (
          <div className="admin-detail-section">
            <h4>Equipment</h4>
            <div className="admin-detail-items">
              {Object.entries(equipment).map(([slot, item]) => (
                <div key={slot} className="admin-item-tag">
                  <strong>{slot}:</strong> {typeof item === 'string' ? item : item?.name || 'None'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inventory */}
        {inventory.length > 0 && (
          <div className="admin-detail-section">
            <h4>Inventory ({inventory.length} items)</h4>
            <div className="admin-detail-items">
              {inventory.map((item, i) => (
                <div key={i} className="admin-item-tag">
                  {item.name} {(item.quantity || 1) > 1 ? `${item.quantity}` : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seeds */}
        {Object.keys(seeds).length > 0 && (
          <div className="admin-detail-section">
            <h4>Seeds</h4>
            <div className="admin-detail-items">
              {Object.entries(seeds).map(([seed, xp]) => (
                <div key={seed} className="admin-item-tag seed">{seed}: {xp} XP</div>
              ))}
            </div>
          </div>
        )}

        {/* Chosen combat deck cards */}
        <div className="admin-detail-section">
          <h4>Chosen Combat Deck Cards ({deckCardDetails.length || deckCards.length})</h4>
          {deckCardDetails.length > 0 ? (
            <div className="admin-detail-items">
              {deckCardDetails.map((card) => (
                <div key={card.key} className="admin-item-tag" title={card.description || card.key}>
                  <strong>{card.name}</strong>
                  {card.rarity ? ` — ${card.rarity}` : ''}
                  <span style={{ color: '#888', marginLeft: 6, fontSize: '0.72rem' }}>({card.key})</span>
                </div>
              ))}
            </div>
          ) : deckCards.length > 0 ? (
            <div className="admin-detail-items">
              {deckCards.map((cardKey) => (
                <div key={cardKey} className="admin-item-tag">
                  {String(cardKey).replace(/_/g, ' ')}
                </div>
              ))}
            </div>
          ) : (
            <p className="admin-muted">No chosen level-up deck cards.</p>
          )}
        </div>

        {/* Pet */}
        {petData && (
          <div className="admin-detail-section">
            <h4>Pet: {petData.name}</h4>
            <div className="admin-detail-stats">
              <div className="admin-detail-stat"><label>HP</label><span>{petData.currentHp}/{petData.hp}</span></div>
              <div className="admin-detail-stat"><label>Lv</label><span>{petData.level}</span></div>
              <div className="admin-detail-stat"><label>ATT</label><span>{petData.att}</span></div>
              <div className="admin-detail-stat"><label>DEF</label><span>{petData.def}</span></div>
            </div>
          </div>
        )}

        {/* Crafting */}
        <div className="admin-detail-section">
          <h4>⚒️ Crafting</h4>
          <div className="admin-detail-stats">
            {['smithing', 'alchemy', 'enchanting'].map(disc => {
              const d = crafting[disc] || { level: 1, xp: 0 };
              const level = Math.floor(Math.sqrt((d.xp || 0) / 150)) + 1;
              const currentLevelXp = (level - 1) * (level - 1) * 150;
              const nextLevelXp = level * level * 150;
              const progress = nextLevelXp > currentLevelXp ? Math.round(((d.xp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100) : 100;
              const icons = { smithing: '⚒️', alchemy: '⚗️', enchanting: '🔮' };
              return (
                <div key={disc} className="admin-detail-stat" style={{ minWidth: '120px' }}>
                  <label>{icons[disc]} {disc.charAt(0).toUpperCase() + disc.slice(1)}</label>
                  <span>Lv {level} ({d.xp || 0} XP)</span>
                  <div className="admin-crafting-bar">
                    <div className="admin-crafting-bar-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="admin-detail-stat">
              <label>📜 Recipes</label>
              <span>{knownRecipes.length} known</span>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="admin-detail-section">
          <h4>Quick Actions</h4>

          <div className="admin-action-row">
            <button className="admin-btn admin-btn-success" onClick={() => handlePlayerAction('heal', p.id)}>
              ❤️ Full Heal
            </button>
          </div>

          <div className="admin-action-row">
            <input type="number" placeholder="Gold amount" value={giveGoldAmount} onChange={e => setGiveGoldAmount(e.target.value)} className="admin-input admin-input-sm" min="1" />
            <button className="admin-btn admin-btn-sm" disabled={!giveGoldAmount || parseInt(giveGoldAmount) <= 0} onClick={() => { handlePlayerAction('give-gold', p.id, { amount: parseInt(giveGoldAmount) }); setGiveGoldAmount(''); }}>
               Give Gold
            </button>
          </div>

          <div className="admin-action-row">
            <input type="number" placeholder="XP amount" value={giveXpAmount} onChange={e => setGiveXpAmount(e.target.value)} className="admin-input admin-input-sm" min="1" />
            <button className="admin-btn admin-btn-sm" disabled={!giveXpAmount || parseInt(giveXpAmount) <= 0} onClick={() => { handlePlayerAction('give-xp', p.id, { amount: parseInt(giveXpAmount) }); setGiveXpAmount(''); }}>
               Give XP
            </button>
          </div>

          <div className="admin-action-row">
            <select value={giveCraftXpDiscipline} onChange={e => setGiveCraftXpDiscipline(e.target.value)} className="admin-select admin-select-sm">
              <option value="smithing">⚒️ Smithing</option>
              <option value="alchemy">⚗️ Alchemy</option>
              <option value="enchanting">🔮 Enchanting</option>
            </select>
            <input type="number" placeholder="Craft XP" value={giveCraftXpAmount} onChange={e => setGiveCraftXpAmount(e.target.value)} className="admin-input admin-input-sm" min="1" />
            <button className="admin-btn admin-btn-sm" disabled={!giveCraftXpAmount || parseInt(giveCraftXpAmount) <= 0} onClick={() => { handlePlayerAction('give-crafting-xp', p.id, { discipline: giveCraftXpDiscipline, amount: parseInt(giveCraftXpAmount) }); setGiveCraftXpAmount(''); }}>
              ⚒️ Give Craft XP
            </button>
          </div>

          <div className="admin-action-row">
            <input type="text" placeholder="Area (e.g. maze6.dat)" value={teleportArea} onChange={e => setTeleportArea(e.target.value)} className="admin-input admin-input-sm" />
            <input type="number" placeholder="X" value={teleportX} onChange={e => setTeleportX(e.target.value)} className="admin-input admin-input-xs" />
            <input type="number" placeholder="Y" value={teleportY} onChange={e => setTeleportY(e.target.value)} className="admin-input admin-input-xs" />
            <button className="admin-btn admin-btn-sm" disabled={!teleportArea || !teleportX || !teleportY} onClick={() => {
              handlePlayerAction('teleport', p.id, { area: teleportArea, x: parseInt(teleportX), y: parseInt(teleportY) });
              setTeleportArea(''); setTeleportX(''); setTeleportY('');
            }}>
               Teleport
            </button>
          </div>

          <div className="admin-action-row">
            <select value={editField} onChange={e => setEditField(e.target.value)} className="admin-select admin-select-sm">
              <option value="">Select field...</option>
              <option value="level">Level</option>
              <option value="experience">XP</option>
              <option value="gold">Gold</option>
              <option value="strength">STR</option>
              <option value="agility">AGL</option>
              <option value="vitality">VIT</option>
              <option value="mentality">MEN</option>
              <option value="charisma">CHA</option>
              <option value="max_hp">Max HP</option>
              <option value="max_mp">Max MP</option>
              <option value="att">ATT</option>
              <option value="def">DEF</option>
              <option value="battle_state">Battle State</option>
              <option value="advanced_class">Advanced Class</option>
              <option value="ac_secondary">AC Secondary</option>
              <option value="ac_abilities">AC Abilities (JSON)</option>
            </select>
            <input type="text" placeholder="New value" value={editValue} onChange={e => setEditValue(e.target.value)} className="admin-input admin-input-sm" />
            <button className="admin-btn admin-btn-sm" disabled={!editField || !editValue} onClick={() => {
              const val = ['level','experience','gold','strength','agility','vitality','mentality','charisma','max_hp','max_mp','att','def'].includes(editField)
                ? parseInt(editValue) : editValue;
              handlePlayerAction('edit', p.id, { field: editField, value: val });
              setEditValue('');
            }}>
               Set
            </button>
          </div>

          <div className="admin-action-row danger-zone">
            <button className="admin-btn admin-btn-warning" onClick={() => setConfirmAction({
              message: `Reset ${p.name} to starting state? This clears XP, gold, inventory, quests, and teleports them to the starting area.`,
              onConfirm: () => handlePlayerAction('reset', p.id)
            })}>
               Reset Character
            </button>
            <button className="admin-btn admin-btn-danger" onClick={() => setConfirmAction({
              message: `PERMANENTLY DELETE ${p.name}'s account (${p.username})? This cannot be undone!`,
              onConfirm: () => handlePlayerAction('delete', p.id)
            })}>
              🗑️ Delete Account
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // MONSTERS TAB
  // ============================================================

  const openMonsterEditor = (monster) => {
    setSelectedMonster(monster);
    setEditMonster({
      ...monster,
      weapon: { ...(monster.weapon || { name: 'Attack', damage: '1d4', att: 0, mtype: 'physical' }) },
      abilities: [...(monster.abilities || [])],
      inv: (monster.inv || []).map(item => ({ ...item })),
      resistances: { ...(monster.resistances || {}) },
      weaknesses: { ...(monster.weaknesses || {}) },
      immunities: [...(monster.immunities || [])]
    });
    setMonsterStatusMsg(null);
  };

  const closeMonsterEditor = () => {
    setSelectedMonster(null);
    setEditMonster(null);
    setMonsterStatusMsg(null);
  };

  const handleMonsterField = (field, value) => {
    setEditMonster(prev => ({ ...prev, [field]: value }));
  };

  const handleMonsterNumField = (field, value) => {
    setEditMonster(prev => ({ ...prev, [field]: value === '' ? '' : parseInt(value) || 0 }));
  };

  const handleWeaponField = (field, value) => {
    setEditMonster(prev => ({
      ...prev,
      weapon: { ...prev.weapon, [field]: field === 'att' ? (parseInt(value) || 0) : value }
    }));
  };

  const handleToggleAbility = (abilityKey) => {
    setEditMonster(prev => {
      const abilities = [...(prev.abilities || [])];
      const idx = abilities.indexOf(abilityKey);
      if (idx >= 0) abilities.splice(idx, 1);
      else abilities.push(abilityKey);
      return { ...prev, abilities };
    });
  };

  const handleEditDrop = (index, field, value) => {
    setEditMonster(prev => {
      const inv = [...(prev.inv || [])];
      if (field === 'cost' || field === 'dropChance') value = parseFloat(value) || 0;
      inv[index] = { ...inv[index], [field]: value };
      return { ...prev, inv };
    });
  };

  const handleAddDrop = () => {
    setEditMonster(prev => ({
      ...prev,
      inv: [...(prev.inv || []), { name: '', cost: 0, type: 'parts', dropChance: 10 }]
    }));
  };

  const handleRemoveDrop = (index) => {
    setEditMonster(prev => {
      const inv = [...(prev.inv || [])];
      inv.splice(index, 1);
      return { ...prev, inv };
    });
  };

  const handleEditResistance = (element, value) => {
    setEditMonster(prev => {
      const resistances = { ...(prev.resistances || {}) };
      if (value === '' || value === null) delete resistances[element];
      else resistances[element] = parseFloat(value) || 0;
      return { ...prev, resistances };
    });
  };

  const handleAddResistance = () => {
    const el = prompt('Element name (e.g. Fire, Cold, Lightning):');
    if (!el) return;
    handleEditResistance(el, 0.5);
  };

  const handleEditWeakness = (element, value) => {
    setEditMonster(prev => {
      const weaknesses = { ...(prev.weaknesses || {}) };
      if (value === '' || value === null) delete weaknesses[element];
      else weaknesses[element] = parseFloat(value) || 0;
      return { ...prev, weaknesses };
    });
  };

  const handleAddWeakness = () => {
    const el = prompt('Element name (e.g. Fire, Cold, Light):');
    if (!el) return;
    handleEditWeakness(el, 1.5);
  };

  const handleAddImmunity = () => {
    const el = prompt('Element name to add immunity for:');
    if (!el) return;
    setEditMonster(prev => ({
      ...prev,
      immunities: [...new Set([...(prev.immunities || []), el])]
    }));
  };

  const handleRemoveImmunity = (element) => {
    setEditMonster(prev => ({
      ...prev,
      immunities: (prev.immunities || []).filter(e => e !== element)
    }));
  };

  const handleSaveMonster = async () => {
    if (!editMonster) return;
    setMonsterSaving(true);
    try {
      await axios.put(`${API}/api/admin/monsters/${encodeURIComponent(editMonster.name)}`, editMonster);
      setMonsterStatusMsg({ text: 'Monster saved successfully!', type: 'success' });
      logAction(`Updated monster: ${editMonster.name}`);
      fetchMonsters();
      setTimeout(() => setMonsterStatusMsg(null), 3000);
    } catch (err) {
      setMonsterStatusMsg({ text: err.response?.data?.error || 'Failed to save', type: 'error' });
    }
    setMonsterSaving(false);
  };

  const handleResetMonster = async () => {
    if (!selectedMonster) return;
    if (!window.confirm(`Reset "${selectedMonster.name}" to base template defaults? This removes all admin overrides.`)) return;
    setMonsterSaving(true);
    try {
      await axios.delete(`${API}/api/admin/monsters/${encodeURIComponent(selectedMonster.name)}/overrides`);
      setMonsterStatusMsg({ text: 'Monster reset to defaults!', type: 'success' });
      logAction(`Reset monster to defaults: ${selectedMonster.name}`);
      fetchMonsters();
      // Refresh edit state with the base data
      const refreshed = await axios.get(`${API}/api/admin/monsters`);
      const base = refreshed.data.find(m => m.name === selectedMonster.name);
      if (base) openMonsterEditor(base);
      setTimeout(() => setMonsterStatusMsg(null), 3000);
    } catch (err) {
      setMonsterStatusMsg({ text: err.response?.data?.error || 'Failed to reset', type: 'error' });
    }
    setMonsterSaving(false);
  };

  const renderMonsterEditor = () => {
    if (!editMonster) return null;
    const abilities = monsterMeta?.abilities || [];
    const abilityTypes = {};
    abilities.forEach(a => {
      if (!abilityTypes[a.type]) abilityTypes[a.type] = [];
      abilityTypes[a.type].push(a);
    });

    return (
      <div className="admin-modal-overlay" onClick={closeMonsterEditor}>
        <div className="admin-monster-editor" onClick={e => e.stopPropagation()}>
          <div className="monster-editor-header">
            <h3>🐉 Edit Monster: {editMonster.name}</h3>
            <div className="monster-editor-header-actions">
              {selectedMonster?.hasOverride && (
                <button className="admin-btn admin-btn-warn" onClick={handleResetMonster} disabled={monsterSaving}>
                  ↺ Reset to Defaults
                </button>
              )}
              <button className="admin-btn" onClick={closeMonsterEditor}>✕</button>
            </div>
          </div>

          {monsterStatusMsg && (
            <div className={`admin-broadcast-status ${monsterStatusMsg.type}`}>
              {monsterStatusMsg.text}
            </div>
          )}

          <div className="monster-editor-body">
            {/* ── Identity & Core Stats ── */}
            <div className="monster-editor-section">
              <h4>⚔️ Core Stats</h4>
              <div className="monster-editor-grid">
                <label>Type
                  <select value={editMonster.type || 'animal'} onChange={e => handleMonsterField('type', e.target.value)}>
                    {(monsterMeta?.monsterTypes || ['animal','humanoid','undead','beast','dragon']).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label>Level<input type="number" value={editMonster.level ?? ''} onChange={e => handleMonsterNumField('level', e.target.value)} min="1" max="100" /></label>
                <label>HP<input type="number" value={editMonster.hp ?? ''} onChange={e => handleMonsterNumField('hp', e.target.value)} min="1" /></label>
                <label>MP<input type="number" value={editMonster.mp ?? ''} onChange={e => handleMonsterNumField('mp', e.target.value)} min="0" /></label>
                <label>ATT<input type="number" value={editMonster.att ?? ''} onChange={e => handleMonsterNumField('att', e.target.value)} /></label>
                <label>DEF<input type="number" value={editMonster.def ?? ''} onChange={e => handleMonsterNumField('def', e.target.value)} /></label>
                <label>DR<input type="number" value={editMonster.dr ?? ''} onChange={e => handleMonsterNumField('dr', e.target.value)} min="0" /></label>
                <label>SR<input type="number" value={editMonster.sr ?? ''} onChange={e => handleMonsterNumField('sr', e.target.value)} min="0" /></label>
              </div>
            </div>

            {/* ── Attributes ── */}
            <div className="monster-editor-section">
              <h4>📊 Attributes</h4>
              <div className="monster-editor-grid">
                <label>STR<input type="number" value={editMonster.STR ?? ''} onChange={e => handleMonsterNumField('STR', e.target.value)} min="1" /></label>
                <label>AGL<input type="number" value={editMonster.AGL ?? ''} onChange={e => handleMonsterNumField('AGL', e.target.value)} min="1" /></label>
                <label>VIT<input type="number" value={editMonster.VIT ?? ''} onChange={e => handleMonsterNumField('VIT', e.target.value)} min="1" /></label>
                <label>MEN<input type="number" value={editMonster.MEN ?? ''} onChange={e => handleMonsterNumField('MEN', e.target.value)} min="1" /></label>
                <label>CHA<input type="number" value={editMonster.CHA ?? ''} onChange={e => handleMonsterNumField('CHA', e.target.value)} min="1" /></label>
              </div>
            </div>

            {/* ── Rewards & Behavior ── */}
            <div className="monster-editor-section">
              <h4>💰 Rewards & Behavior</h4>
              <div className="monster-editor-grid">
                <label>XP<input type="number" value={editMonster.xp ?? ''} onChange={e => handleMonsterNumField('xp', e.target.value)} min="0" /></label>
                <label>GP<input type="number" value={editMonster.gp ?? ''} onChange={e => handleMonsterNumField('gp', e.target.value)} min="0" /></label>
                <label>Aggression<input type="number" value={editMonster.aggr ?? ''} onChange={e => handleMonsterNumField('aggr', e.target.value)} min="0" max="100" /></label>
                <label className="monster-editor-checkbox">
                  <input type="checkbox" checked={!!editMonster.charm} onChange={e => handleMonsterField('charm', e.target.checked)} />
                  Charmable
                </label>
                <label>Align G↔E<input type="number" value={editMonster.alignGE ?? 0} onChange={e => handleMonsterNumField('alignGE', e.target.value)} min="-100" max="100" /></label>
                <label>Align L↔C<input type="number" value={editMonster.alignLC ?? 0} onChange={e => handleMonsterNumField('alignLC', e.target.value)} min="-100" max="100" /></label>
              </div>
            </div>

            {/* ── Weapon ── */}
            <div className="monster-editor-section">
              <h4>🗡️ Weapon</h4>
              <div className="monster-editor-grid">
                <label>Name<input type="text" value={editMonster.weapon?.name || ''} onChange={e => handleWeaponField('name', e.target.value)} /></label>
                <label>Damage<input type="text" value={editMonster.weapon?.damage || ''} onChange={e => handleWeaponField('damage', e.target.value)} placeholder="e.g. 2d6" /></label>
                <label>ATT Bonus<input type="number" value={editMonster.weapon?.att ?? 0} onChange={e => handleWeaponField('att', e.target.value)} /></label>
                <label>Type<input type="text" value={editMonster.weapon?.mtype || ''} onChange={e => handleWeaponField('mtype', e.target.value)} placeholder="physical" /></label>
              </div>
            </div>

            {/* ── Abilities ── */}
            <div className="monster-editor-section">
              <h4>✨ Abilities</h4>
              <div className="monster-editor-abilities">
                {Object.entries(abilityTypes).map(([type, abils]) => (
                  <div key={type} className="ability-type-group">
                    <span className="ability-type-label">{type}</span>
                    {abils.map(a => (
                      <label key={a.key} className={`ability-chip ${(editMonster.abilities || []).includes(a.key) ? 'active' : ''}`}>
                        <input
                          type="checkbox"
                          checked={(editMonster.abilities || []).includes(a.key)}
                          onChange={() => handleToggleAbility(a.key)}
                        />
                        {a.name}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Item Drops ── */}
            <div className="monster-editor-section">
              <h4>🎒 Item Drops</h4>
              {(editMonster.inv || []).length > 0 && (
                <table className="monster-drops-table">
                  <thead>
                    <tr><th>Item Name</th><th>Value</th><th>Type</th><th>Drop %</th><th></th></tr>
                  </thead>
                  <tbody>
                    {(editMonster.inv || []).map((item, idx) => (
                      <tr key={idx}>
                        <td><input type="text" value={item.name} onChange={e => handleEditDrop(idx, 'name', e.target.value)} /></td>
                        <td><input type="number" value={item.cost} onChange={e => handleEditDrop(idx, 'cost', e.target.value)} min="0" /></td>
                        <td>
                          <select value={item.type} onChange={e => handleEditDrop(idx, 'type', e.target.value)}>
                            <option value="parts">parts</option>
                            <option value="weapon">weapon</option>
                            <option value="armor">armor</option>
                            <option value="potion">potion</option>
                            <option value="scroll">scroll</option>
                            <option value="gem">gem</option>
                            <option value="misc">misc</option>
                          </select>
                        </td>
                        <td><input type="number" value={item.dropChance} onChange={e => handleEditDrop(idx, 'dropChance', e.target.value)} min="0" max="100" step="0.5" /></td>
                        <td><button className="admin-btn admin-btn-small admin-btn-danger" onClick={() => handleRemoveDrop(idx)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <button className="admin-btn admin-btn-small" onClick={handleAddDrop}>+ Add Drop</button>
            </div>

            {/* ── Elemental ── */}
            <div className="monster-editor-section">
              <h4>🔥 Elemental Properties</h4>
              <div className="monster-editor-elemental">
                <div className="elemental-group">
                  <span className="elemental-label">Resistances <small>(multiplier &lt; 1 = less damage)</small></span>
                  {Object.entries(editMonster.resistances || {}).map(([el, val]) => (
                    <div key={el} className="elemental-row">
                      <span className="elemental-name">{el}</span>
                      <input type="number" value={val} onChange={e => handleEditResistance(el, e.target.value)} step="0.05" min="0" max="1" />
                      <button className="admin-btn admin-btn-small admin-btn-danger" onClick={() => handleEditResistance(el, null)}>✕</button>
                    </div>
                  ))}
                  <button className="admin-btn admin-btn-small" onClick={handleAddResistance}>+ Add</button>
                </div>
                <div className="elemental-group">
                  <span className="elemental-label">Weaknesses <small>(multiplier &gt; 1 = more damage)</small></span>
                  {Object.entries(editMonster.weaknesses || {}).map(([el, val]) => (
                    <div key={el} className="elemental-row">
                      <span className="elemental-name">{el}</span>
                      <input type="number" value={val} onChange={e => handleEditWeakness(el, e.target.value)} step="0.1" min="1" max="5" />
                      <button className="admin-btn admin-btn-small admin-btn-danger" onClick={() => handleEditWeakness(el, null)}>✕</button>
                    </div>
                  ))}
                  <button className="admin-btn admin-btn-small" onClick={handleAddWeakness}>+ Add</button>
                </div>
                <div className="elemental-group">
                  <span className="elemental-label">Immunities <small>(full immunity, 0 damage)</small></span>
                  <div className="immunity-tags">
                    {(editMonster.immunities || []).map(el => (
                      <span key={el} className="immunity-tag">{el} <button onClick={() => handleRemoveImmunity(el)}>✕</button></span>
                    ))}
                  </div>
                  <button className="admin-btn admin-btn-small" onClick={handleAddImmunity}>+ Add</button>
                </div>
              </div>
            </div>
          </div>

          <div className="monster-editor-footer">
            <button className="admin-btn" onClick={closeMonsterEditor}>Cancel</button>
            <button className="admin-btn admin-btn-primary" onClick={handleSaveMonster} disabled={monsterSaving}>
              {monsterSaving ? 'Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMonsters = () => {
    const filtered = monsters.filter(m =>
      m.name.toLowerCase().includes(monsterSearch.toLowerCase())
    );
    return (
      <div className="admin-data-tab">
        <div className="admin-search-bar">
          <input
            type="text"
            placeholder="Search monsters..."
            value={monsterSearch}
            onChange={e => setMonsterSearch(e.target.value)}
            className="admin-input admin-search-input"
          />
          <span className="admin-count">{filtered.length} of {monsters.length} monsters</span>
        </div>
        <div className="admin-data-grid">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th><th>Lv</th><th>HP</th><th>MP</th>
                <th>ATT</th><th>DEF</th><th>DR</th><th>SR</th>
                <th>XP</th><th>GP</th><th>Type</th><th>Charm</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={i} className={`admin-monster-row ${m.hasOverride ? 'admin-monster-overridden' : ''}`} onClick={() => openMonsterEditor(m)} title="Click to edit">
                  <td><strong>{m.name}</strong>{m.hasOverride && <span className="admin-override-badge" title="Admin modified">✎</span>}</td>
                  <td>{m.level}</td>
                  <td>{m.hp}</td>
                  <td>{m.mp}</td>
                  <td>{m.att}</td>
                  <td>{m.def}</td>
                  <td>{m.dr}</td>
                  <td>{m.sr}</td>
                  <td>{m.xp}</td>
                  <td>{m.gp}</td>
                  <td className="admin-type-col">{m.type}</td>
                  <td>{m.charm ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderMonsterEditor()}
      </div>
    );
  };

  // ============================================================
  // ITEMS TAB
  // ============================================================
  const renderItems = () => {
    const allItems = Object.entries(items).filter(([name]) =>
      name.toLowerCase().includes(itemSearch.toLowerCase())
    );
    return (
      <div className="admin-data-tab">
        <div className="admin-search-bar">
          <input
            type="text"
            placeholder="Search items..."
            value={itemSearch}
            onChange={e => setItemSearch(e.target.value)}
            className="admin-input admin-search-input"
          />
          <span className="admin-count">{allItems.length} of {Object.keys(items).length} items</span>
          {selectedPlayer && (
            <span className="admin-give-hint">Click an item to give it to {selectedPlayer.name}</span>
          )}
        </div>
        <div className="admin-data-grid">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th><th>Type</th><th>Cost</th><th>Level</th><th>Details</th>
                {selectedPlayer && <th>Give</th>}
              </tr>
            </thead>
            <tbody>
              {allItems.map(([name, item]) => (
                <tr key={name}>
                  <td><strong>{name}</strong></td>
                  <td>{item.type}</td>
                  <td>{item.cost}</td>
                  <td>{item.level || '-'}</td>
                  <td className="admin-item-details">
                    {item.damage && `Dmg: ${item.damage}`}
                    {item.hp > 0 && ` HP+${item.hp}`}
                    {item.mp > 0 && ` MP+${item.mp}`}
                    {item.att > 0 && ` ATT+${item.att}`}
                    {item.bonus > 0 && ` Bonus+${item.bonus}`}
                    {item.size && ` (${item.size})`}
                    {item.slot && ` [${item.slot}]`}
                    {item.description && !item.damage && !item.hp && item.description}
                  </td>
                  {selectedPlayer && (
                    <td>
                      <button className="admin-btn admin-btn-xs" onClick={() => handlePlayerAction('give-item', selectedPlayer.id, { item })}>
                        </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================================
  // TOOLS TAB
  // ============================================================
  // ── Account detail + admin toggle ──
  const selectAccount = async (account) => {
    try {
      const res = await axios.get(`${API}/api/admin/accounts/${account.id}`);
      setSelectedAccount(res.data);
    } catch (err) {
      logAction('Failed to load account details');
    }
  };

  const toggleAccountAdmin = async (accountId, currentIsAdmin) => {
    if (Number(accountId) === Number(user.id) && currentIsAdmin) {
      flashPlayerStatus('Cannot remove your own admin status', 'error');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/admin/toggle-admin`, { userId: accountId, isAdmin: !currentIsAdmin });
      logAction(res.data.message);
      flashPlayerStatus(res.data.message, 'success');
      fetchAccounts();
      // Refresh selected account detail
      if (selectedAccount && selectedAccount.id === accountId) {
        const detail = await axios.get(`${API}/api/admin/accounts/${accountId}`);
        setSelectedAccount(detail.data);
      }
    } catch (err) {
      flashPlayerStatus(err.response?.data?.error || 'Failed to toggle admin', 'error');
    }
  };

  const renderAccountDetail = () => {
    const a = selectedAccount;
    if (!a) return null;

    return (
      <div className="admin-detail-content">
        <div className="admin-detail-header">
          <h3>{a.username}</h3>
          <span className="admin-detail-subtitle">
            {a.is_admin ? '✦ Admin' : 'Player'} — {a.email}
          </span>
        </div>

        {/* Action status message */}
        {playerStatusMsg && (
          <div className={`admin-player-status ${playerStatusMsg.type}`}>
            {playerStatusMsg.type === 'success' ? '✓' : '✗'} {playerStatusMsg.text}
          </div>
        )}

        {/* Account info */}
        <div className="admin-detail-stats">
          <div className="admin-detail-stat">
            <label>Account ID</label>
            <span>{a.id}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Admin</label>
            <span style={{ color: a.is_admin ? '#d4af37' : '#888' }}>{a.is_admin ? 'Yes' : 'No'}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Characters</label>
            <span>{a.characters?.length || 0} / 5</span>
          </div>
          <div className="admin-detail-stat">
            <label>Created</label>
            <span>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Last Login</label>
            <span>{a.last_login ? timeSince(a.last_login) : 'Never'}</span>
          </div>
          <div className="admin-detail-stat">
            <label>Last Action</label>
            <span>{a.last_character_action ? timeSince(a.last_character_action) : 'Never'}</span>
          </div>
        </div>

        {/* Characters list */}
        {a.characters && a.characters.length > 0 && (
          <div className="admin-detail-section">
            <h4>Characters ({a.characters.length})</h4>
            {a.characters.map(c => (
              <div key={c.id} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 4, padding: '0.5rem', marginBottom: '0.4rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontWeight: 600, color: '#c9a84c', fontSize: '0.9rem' }}>{c.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: '0.3rem' }}>
                  Lv{c.level} {c.race} {c.class} — {c.alignment}
                </div>
                <div className="admin-detail-stats" style={{ fontSize: '0.7rem' }}>
                  <div className="admin-detail-stat">
                    <label>HP</label>
                    <span>{(c.max_hp - (c.damage || 0))}/{c.max_hp}</span>
                  </div>
                  <div className="admin-detail-stat">
                    <label>MP</label>
                    <span>{(c.max_mp - (c.used_mp || 0))}/{c.max_mp}</span>
                  </div>
                  <div className="admin-detail-stat">
                    <label>XP</label>
                    <span>{c.experience?.toLocaleString()}</span>
                  </div>
                  <div className="admin-detail-stat">
                    <label>Gold</label>
                    <span>{c.gold?.toLocaleString()}</span>
                  </div>
                  <div className="admin-detail-stat">
                    <label>Area</label>
                    <span>{c.current_area}</span>
                  </div>
                  <div className="admin-detail-stat">
                    <label>Last Active</label>
                    <span>{timeSince(c.last_action)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {a.characters?.length === 0 && (
          <div className="admin-detail-section">
            <p className="admin-muted">No characters on this account</p>
          </div>
        )}

        {/* Admin toggle */}
        <div className="admin-detail-section">
          <h4>Actions</h4>
          <div className="admin-action-row">
            {a.id === user.id ? (
              <span className="admin-muted">Cannot modify your own admin status</span>
            ) : (
              <button
                className={`admin-btn ${a.is_admin ? 'admin-btn-warning' : 'admin-btn-success'}`}
                onClick={() => toggleAccountAdmin(a.id, a.is_admin)}
              >
                {a.is_admin ? '➖ Remove Admin' : '✨ Make Admin'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAccounts = () => (
    <div className="admin-players">
      <div className="admin-players-list">
        <div className="admin-search-bar">
          <input
            className="admin-input admin-search-input"
            placeholder="Search by username or email..."
            value={accountSearch}
            onChange={e => setAccountSearch(e.target.value)}
          />
          <span className="admin-muted" style={{ marginLeft: 8 }}>{accounts.length} account(s)</span>
        </div>
        <div className="admin-player-table-wrapper">
          <table className="admin-table admin-player-table">
            <thead>
              <tr>
                <SortTh field="username" label="Username" currentSort={accountSortField} currentOrder={accountSortOrder} onSort={handleAccountSort} />
                <SortTh field="email" label="Email" currentSort={accountSortField} currentOrder={accountSortOrder} onSort={handleAccountSort} />
                <th>Admin</th>
                <SortTh field="character_count" label="Chars" currentSort={accountSortField} currentOrder={accountSortOrder} onSort={handleAccountSort} />
                <SortTh field="last_login" label="Last Login" currentSort={accountSortField} currentOrder={accountSortOrder} onSort={handleAccountSort} />
              </tr>
            </thead>
            <tbody>
              {sortedAccounts.map(a => (
                <tr
                  key={a.id}
                  className={selectedAccount?.id === a.id ? 'selected' : ''}
                  style={a.is_admin ? { background: 'rgba(212,175,55,0.08)' } : {}}
                  onClick={() => selectAccount(a)}
                >
                  <td style={{ fontWeight: 600 }}>{a.username}</td>
                  <td style={{ fontSize: '0.8rem', color: '#aaa' }}>{a.email}</td>
                  <td>{a.is_admin ? <span style={{ color: '#d4af37', fontWeight: 700 }}>✦</span> : <span style={{ color: '#666' }}>—</span>}</td>
                  <td>{a.character_count}</td>
                  <td style={{ fontSize: '0.75rem', color: '#888' }}>{a.last_login ? timeSince(a.last_login) : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Account detail panel */}
      <div className="admin-player-detail">
        {selectedAccount ? renderAccountDetail() : (
          <div className="admin-no-selection">
            <p>Select an account to view details</p>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================
  // MODERATION TAB
  // ============================================================

  const handleModAction = async (action, characterId, body = null) => {
    try {
      const res = await axios.post(
        `${API}/api/admin/moderation/${characterId}/${action}`,
        body || {}
      );
      logAction(res.data.message);
      fetchModeration();
      fetchModerationLog();
    } catch (err) {
      logAction(`Moderation error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Prompt-based mute / kick / account-ban actions
  const promptMute = (characterId, defaultName = 'player') => {
    const raw = window.prompt(
      `Mute ${defaultName} for how many minutes? (e.g. 60, 1440, 10080)`,
      '1440'
    );
    if (raw === null) return;
    const minutes = parseInt(raw, 10);
    if (!Number.isFinite(minutes) || minutes < 1) return;
    const reason = window.prompt('Reason? (shown in system DM; optional)', '') || '';
    handleModAction('mute', characterId, { durationMinutes: minutes, reason });
  };

  const promptKick = (characterId, defaultName = 'player') => {
    const reason = window.prompt(`Kick ${defaultName} — reason? (optional)`, '');
    if (reason === null) return;
    handleModAction('kick', characterId, { reason });
  };

  const promptAccountBan = (characterId, defaultName = 'player') => {
    if (!window.confirm(`Ban the account associated with ${defaultName}? This disconnects all their characters immediately.`)) return;
    const reason = window.prompt('Ban reason? (optional)', '') || '';
    handleModAction('account-ban', characterId, { reason });
  };

  const promptAccountUnban = (characterId, defaultName = 'player') => {
    if (!window.confirm(`Lift the account ban for ${defaultName}?`)) return;
    handleModAction('account-unban', characterId, {});
  };

  const exportAuditLogCsv = () => {
    if (!auditLog.length) return;
    const header = ['id','created_at','actor_type','actor_name','actor_user_id','event_category','event_type','target_type','target_id','target_name','ip_address','metadata'];
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = auditLog.map(e => header.map(h => esc(e[h])).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Direct action form — act on a character by name (not limited to offenders)
  const runDirectAction = async () => {
    const name = (directActionName || '').trim();
    if (!name) { logAction('Enter a character name.'); return; }
    const action = directAction;
    try {
      // Resolve character by name
      const look = await axios.get(`${API}/api/admin/players`, {
        params: { search: name, limit: 5 }
      });
      const hit = (look.data.players || []).find(p =>
        p.name.toLowerCase() === name.toLowerCase()
      ) || (look.data.players || [])[0];
      if (!hit) { logAction(`No character matched "${name}".`); return; }

      const reason = directActionReason || '';
      if (action === 'mute') {
        const minutes = parseInt(directActionDuration, 10) || 1440;
        await handleModAction('mute', hit.id, { durationMinutes: minutes, reason });
      } else if (action === 'kick') {
        await handleModAction('kick', hit.id, { reason });
      } else if (action === 'unban') {
        await handleModAction('unban', hit.id);
      } else if (action === 'account-ban') {
        if (!window.confirm(`Ban the account for ${hit.name}?`)) return;
        await handleModAction('account-ban', hit.id, { reason });
      } else if (action === 'account-unban') {
        await handleModAction('account-unban', hit.id, {});
      }
    } catch (err) {
      logAction(`Direct action error: ${err.response?.data?.error || err.message}`);
    }
  };

  const renderModeration = () => {
    const warned = modPlayers.filter(p => p.status === 'warned');
    const banned = modPlayers.filter(p => p.status === 'banned');
    const allOffenders = modPlayers.filter(p => p.chatOffenses > 0);

    return (
      <div className="admin-tools-tab">
        {/* Direct moderation action — act on any character by name */}
        <div className="admin-panel" style={{ background: '#1a1a2e' }}>
          <h3>🛠 Direct Moderation Action</h3>
          <p className="admin-muted" style={{ marginBottom: 8 }}>
            Act on any character directly by name — does not require prior offenses.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Character name…"
              value={directActionName}
              onChange={e => setDirectActionName(e.target.value)}
              style={{ flex: '1 1 180px', background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: '6px 8px' }}
            />
            <select
              value={directAction}
              onChange={e => setDirectAction(e.target.value)}
              style={{ background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: '6px 8px' }}
            >
              <option value="mute">🔇 Mute</option>
              <option value="kick">👢 Kick</option>
              <option value="unban">♻️ Clear chat ban</option>
              <option value="account-ban">⛔ Account ban</option>
              <option value="account-unban">🔓 Account unban</option>
            </select>
            {directAction === 'mute' && (
              <select
                value={directActionDuration}
                onChange={e => setDirectActionDuration(e.target.value)}
                style={{ background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: '6px 8px' }}
              >
                <option value="60">1 hour</option>
                <option value="360">6 hours</option>
                <option value="1440">24 hours</option>
                <option value="10080">7 days</option>
                <option value="43200">30 days</option>
              </select>
            )}
            <input
              type="text"
              placeholder="Reason (optional)"
              value={directActionReason}
              onChange={e => setDirectActionReason(e.target.value)}
              style={{ flex: '2 1 220px', background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: '6px 8px' }}
            />
            <button className="admin-btn admin-btn-danger" onClick={runDirectAction}>Apply</button>
          </div>
        </div>
        {/* Currently Banned */}
        <div className="admin-panel">
          <h3>🚫 Currently Banned ({banned.length})</h3>
          {banned.length === 0 ? (
            <p className="admin-muted">No players are currently chat-banned.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Account</th>
                  <th>Class</th>
                  <th>Lvl</th>
                  <th>All-Time Offenses</th>
                  <th>Ban Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banned.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600, color: '#e74c3c' }}>{p.name}</td>
                    <td style={{ fontSize: '0.8rem', color: '#aaa' }}>{p.username}</td>
                    <td>{p.class}</td>
                    <td>{p.level}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#e74c3c' }}>{p.chatOffenses}</td>
                    <td style={{ fontSize: '0.8rem' }}>{new Date(p.chatBannedUntil).toLocaleString()}</td>
                    <td>
                      <button className="admin-btn admin-btn-sm" onClick={() => handleModAction('unban', p.id)}>Unban</button>
                      {' '}
                      <button className="admin-btn admin-btn-sm" onClick={() => promptKick(p.id, p.name)}>Kick</button>
                      {' '}
                      <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => promptAccountBan(p.id, p.name)}>Account Ban</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Active Warnings */}
        <div className="admin-panel">
          <h3>⚠️ Active Warnings ({warned.length})</h3>
          <p className="admin-muted" style={{ marginBottom: 8 }}>Players warned in the last 24 hours. A second offense will trigger a ban.</p>
          {warned.length === 0 ? (
            <p className="admin-muted">No players have active warnings.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Account</th>
                  <th>Class</th>
                  <th>Lvl</th>
                  <th>All-Time Offenses</th>
                  <th>Warned At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {warned.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600, color: '#f39c12' }}>{p.name}</td>
                    <td style={{ fontSize: '0.8rem', color: '#aaa' }}>{p.username}</td>
                    <td>{p.class}</td>
                    <td>{p.level}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#f39c12' }}>{p.chatOffenses}</td>
                    <td style={{ fontSize: '0.8rem' }}>{new Date(p.chatWarningAt).toLocaleString()}</td>
                    <td>
                      <button className="admin-btn admin-btn-sm" onClick={() => handleModAction('clear-warning', p.id)}>Clear Warning</button>
                      {' '}
                      <button className="admin-btn admin-btn-sm" onClick={() => promptMute(p.id, p.name)}>Mute</button>
                      {' '}
                      <button className="admin-btn admin-btn-sm" onClick={() => promptKick(p.id, p.name)}>Kick</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* All-Time Offenders */}
        <div className="admin-panel">
          <h3>📋 All-Time Offenses ({allOffenders.length})</h3>
          {allOffenders.length === 0 ? (
            <p className="admin-muted">No players have any recorded offenses.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Account</th>
                  <th>Class</th>
                  <th>Lvl</th>
                  <th>Total Offenses</th>
                  <th>Current Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allOffenders.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td style={{ fontSize: '0.8rem', color: '#aaa' }}>{p.username}</td>
                    <td>{p.class}</td>
                    <td>{p.level}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: p.chatOffenses >= 5 ? '#e74c3c' : p.chatOffenses >= 3 ? '#f39c12' : '#888' }}>
                      {p.chatOffenses}
                    </td>
                    <td>
                      {p.status === 'banned' && <span style={{ color: '#e74c3c', fontWeight: 600 }}>🚫 Banned</span>}
                      {p.status === 'warned' && <span style={{ color: '#f39c12', fontWeight: 600 }}>⚠️ Warned</span>}
                      {p.status === 'clean' && <span style={{ color: '#888' }}>—</span>}
                    </td>
                    <td>
                      <button className="admin-btn admin-btn-sm" onClick={() => promptMute(p.id, p.name)}>Mute</button>
                      {' '}
                      <button className="admin-btn admin-btn-sm" onClick={() => promptKick(p.id, p.name)}>Kick</button>
                      {' '}
                      <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => setConfirmAction({
                        message: `Reset ALL offense data for ${p.name}? This clears their offense count, warning, and ban.`,
                        onConfirm: () => handleModAction('reset-offenses', p.id)
                      })}>Reset</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {modLoading && <p className="admin-muted">Loading...</p>}

        {/* Moderation audit log — recent admin actions */}
        <div className="admin-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>📜 Moderation Log (last {modLog.length} of {modLogTotal})</h3>
            <button className="admin-btn admin-btn-sm" onClick={fetchModerationLog}>🔄 Refresh</button>
          </div>
          {modLog.length === 0 ? (
            <p className="admin-muted">No moderation actions recorded yet.</p>
          ) : (
            <table className="admin-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Duration</th>
                  <th>Reason</th>
                  <th>Report</th>
                </tr>
              </thead>
              <tbody>
                {modLog.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontSize: '0.8rem', color: '#aaa' }}>{new Date(e.created_at).toLocaleString()}</td>
                    <td style={{ fontSize: '0.85rem' }}>{e.admin_username || `#${e.admin_id || '?'}`}</td>
                    <td><span style={{ fontWeight: 600, color: '#f0c060' }}>{e.action}</span></td>
                    <td>{e.target_character_name || `char#${e.target_character_id || '?'}`}</td>
                    <td style={{ fontSize: '0.85rem', color: '#aaa' }}>
                      {e.duration_minutes ? (e.duration_minutes >= 60 ? `${Math.round(e.duration_minutes / 60)}h` : `${e.duration_minutes}m`) : '—'}
                    </td>
                    <td style={{ fontSize: '0.85rem', maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.reason || ''}>
                      {e.reason || '—'}
                    </td>
                    <td style={{ fontSize: '0.85rem', color: '#aaa' }}>
                      {e.related_report_id ? `#${e.related_report_id}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // AUDIT LOG TAB (Phase 4)
  // ============================================================
  const renderAuditLog = () => {
    const fmtTime = (t) => { try { return new Date(t).toLocaleString(); } catch { return t; } };
    const catColor = (c) => ({
      admin: '#f0c060',
      auth: '#60b0f0',
      safety: '#c080f0',
      system: '#888',
    }[c] || '#aaa');
    const actorLabel = (e) => {
      const base = e.actor_name || (e.actor_user_id ? `user#${e.actor_user_id}` : 'system');
      return `${base}${e.actor_type === 'admin' ? ' ⚡' : ''}`;
    };
    const targetLabel = (e) => {
      if (!e.target_type && !e.target_id) return '—';
      return `${e.target_name || ''}${e.target_id ? ` (${e.target_type || 'id'}#${e.target_id})` : ''}`.trim() || '—';
    };
    const metaPreview = (m) => {
      if (!m) return '';
      try {
        const s = typeof m === 'string' ? m : JSON.stringify(m);
        return s.length > 80 ? `${s.slice(0, 77)}…` : s;
      } catch { return ''; }
    };
    const totalPages = Math.max(1, Math.ceil(auditTotal / AUDIT_PAGE_SIZE));

    return (
      <div>
        <div className="admin-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>📜 Audit Log ({auditTotal} events)</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="admin-btn admin-btn-sm" onClick={fetchAuditLog}>🔄 Refresh</button>
              <button className="admin-btn admin-btn-sm" onClick={exportAuditLogCsv} disabled={!auditLog.length}>⬇️ Export CSV</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12, alignItems: 'center' }}>
            <label style={{ fontSize: '0.85rem' }}>
              Category:{' '}
              <select
                value={auditFilterCategory}
                onChange={(e) => { setAuditPage(0); setAuditFilterCategory(e.target.value); }}
              >
                <option value="all">All</option>
                <option value="admin">admin</option>
                <option value="auth">auth</option>
                <option value="safety">safety</option>
                <option value="system">system</option>
              </select>
            </label>
            <label style={{ fontSize: '0.85rem' }}>
              Event type prefix:{' '}
              <input
                type="text"
                value={auditFilterEventType}
                placeholder="e.g. login_ or post.players."
                onChange={(e) => setAuditFilterEventType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setAuditPage(0); fetchAuditLog(); } }}
                style={{ width: 200 }}
              />
            </label>
            <label style={{ fontSize: '0.85rem' }}>
              Search:{' '}
              <input
                type="text"
                value={auditFilterSearch}
                placeholder="actor / target / event"
                onChange={(e) => setAuditFilterSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setAuditPage(0); fetchAuditLog(); } }}
                style={{ width: 200 }}
              />
            </label>
            <button
              className="admin-btn admin-btn-sm"
              onClick={() => { setAuditPage(0); fetchAuditLog(); }}
            >Apply</button>
            <button
              className="admin-btn admin-btn-sm"
              onClick={() => {
                setAuditFilterCategory('all');
                setAuditFilterEventType('');
                setAuditFilterSearch('');
                setAuditPage(0);
              }}
            >Clear</button>
          </div>

          {auditLoading && <p className="admin-muted" style={{ marginTop: 8 }}>Loading…</p>}

          {!auditLoading && auditLog.length === 0 ? (
            <p className="admin-muted" style={{ marginTop: 12 }}>No audit events match the current filters.</p>
          ) : (
            <table className="admin-table" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>When</th>
                  <th>Actor</th>
                  <th style={{ width: 90 }}>Category</th>
                  <th>Event</th>
                  <th>Target</th>
                  <th style={{ width: 120 }}>IP</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontSize: '0.8rem', color: '#aaa', whiteSpace: 'nowrap' }}>{fmtTime(e.created_at)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{actorLabel(e)}</td>
                    <td>
                      <span style={{
                        fontWeight: 600,
                        color: catColor(e.event_category),
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                      }}>{e.event_category}</span>
                    </td>
                    <td style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{e.event_type}</td>
                    <td style={{ fontSize: '0.85rem' }}>{targetLabel(e)}</td>
                    <td style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>{e.ip_address || '—'}</td>
                    <td
                      style={{ fontSize: '0.75rem', color: '#aaa', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}
                      title={e.metadata ? (typeof e.metadata === 'string' ? e.metadata : JSON.stringify(e.metadata, null, 2)) : ''}
                    >
                      {metaPreview(e.metadata)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span className="admin-muted" style={{ fontSize: '0.85rem' }}>
              Page {auditPage + 1} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="admin-btn admin-btn-sm"
                disabled={auditPage === 0}
                onClick={() => setAuditPage(p => Math.max(0, p - 1))}
              >← Prev</button>
              <button
                className="admin-btn admin-btn-sm"
                disabled={auditPage + 1 >= totalPages}
                onClick={() => setAuditPage(p => p + 1)}
              >Next →</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // CRAFTING TAB
  // ============================================================
  const fetchCraftingAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/crafting-analytics`);
      setCraftingAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch crafting analytics:', err);
    }
  };

  const fetchCraftingPlayer = async (id) => {
    try {
      const res = await axios.get(`${API}/api/admin/players/${id}/crafting`);
      setCraftingPlayerData(res.data);
    } catch (err) {
      setCraftingStatusMsg({ text: err.response?.data?.error || 'Player not found', type: 'error' });
      setCraftingPlayerData(null);
    }
  };

  const handleCraftingAction = async (action, playerId) => {
    try {
      let res;
      switch (action) {
        case 'give-xp':
          res = await axios.post(`${API}/api/admin/players/${playerId}/give-crafting-xp`, {
            discipline: craftingDiscipline,
            amount: parseInt(craftingXpAmount)
          });
          break;
        case 'set-level':
          res = await axios.post(`${API}/api/admin/players/${playerId}/set-crafting-level`, {
            discipline: craftingDiscipline,
            level: parseInt(craftingLevelTarget)
          });
          break;
        case 'reset':
          res = await axios.post(`${API}/api/admin/players/${playerId}/reset-crafting`);
          break;
        default: return;
      }
      setCraftingStatusMsg({ text: res.data.message, type: 'success' });
      logAction(res.data.message);
      setCraftingPlayerData(prev => prev ? { ...prev, crafting: res.data.crafting, knownRecipes: res.data.knownRecipes } : prev);
      setTimeout(() => setCraftingStatusMsg(null), 5000);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      setCraftingStatusMsg({ text: errMsg, type: 'error' });
      setTimeout(() => setCraftingStatusMsg(null), 8000);
    }
  };

  const renderCrafting = () => {
    // Auto-fetch analytics on first render
    if (!craftingAnalytics) fetchCraftingAnalytics();

    const xpForLevel = (lv) => (lv - 1) * (lv - 1) * 150;
    const calcLevel = (xp) => Math.floor(Math.sqrt((xp || 0) / 150)) + 1;

    return (
      <div className="admin-crafting-tab">
        {/* Analytics Overview */}
        <div className="admin-panel">
          <h3>📊 Crafting Analytics</h3>
          {craftingAnalytics ? (
            <div className="admin-crafting-analytics">
              <div className="admin-analytics-row">
                <div className="admin-analytics-card">
                  <div className="analytics-number">{craftingAnalytics.totalCrafters}</div>
                  <div className="analytics-label">Total Crafters</div>
                </div>
                <div className="admin-analytics-card">
                  <div className="analytics-number">{craftingAnalytics.recipes.avg}</div>
                  <div className="analytics-label">Avg Recipes Known</div>
                </div>
                <div className="admin-analytics-card">
                  <div className="analytics-number">{craftingAnalytics.recipes.max}</div>
                  <div className="analytics-label">Max Recipes Known</div>
                </div>
              </div>

              <h4 style={{ margin: '12px 0 8px' }}>Discipline Averages</h4>
              <div className="admin-analytics-row">
                {['smithing', 'alchemy', 'enchanting'].map(disc => {
                  const d = craftingAnalytics.disciplines[disc];
                  const icons = { smithing: '⚒️', alchemy: '⚗️', enchanting: '🔮' };
                  return (
                    <div key={disc} className="admin-analytics-card">
                      <div className="analytics-icon">{icons[disc]}</div>
                      <div className="analytics-label">{disc.charAt(0).toUpperCase() + disc.slice(1)}</div>
                      <div className="analytics-number">Avg Lv {d.avg}</div>
                      <div className="analytics-sub">Max Lv {d.max}</div>
                    </div>
                  );
                })}
              </div>

              {/* Top Crafters */}
              {craftingAnalytics.topCrafters.length > 0 && (
                <>
                  <h4 style={{ margin: '12px 0 8px' }}>🏆 Top Crafters</h4>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th><th>⚒️ Smith</th><th>⚗️ Alch</th><th>🔮 Ench</th><th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {craftingAnalytics.topCrafters.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: i === 0 ? 700 : 400, color: i === 0 ? '#f1c40f' : '#ddd' }}>{c.name}</td>
                          <td>{c.smithing}</td>
                          <td>{c.alchemy}</td>
                          <td>{c.enchanting}</td>
                          <td style={{ fontWeight: 600 }}>{c.total_level}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <button className="admin-btn admin-btn-sm" style={{ marginTop: 8 }} onClick={fetchCraftingAnalytics}>🔄 Refresh</button>
            </div>
          ) : (
            <p className="admin-muted">Loading analytics...</p>
          )}
        </div>

        {/* Player Crafting Manager */}
        <div className="admin-panel">
          <h3>🔧 Player Crafting Manager</h3>

          {craftingStatusMsg && (
            <div className={`admin-player-status ${craftingStatusMsg.type}`}>
              {craftingStatusMsg.type === 'success' ? '✅' : '❌'} {craftingStatusMsg.text}
            </div>
          )}

          <div className="admin-action-row" style={{ marginBottom: 12 }}>
            <input type="number" placeholder="Player ID" value={craftingPlayerId}
              onChange={e => setCraftingPlayerId(e.target.value)} className="admin-input admin-input-sm" />
            <button className="admin-btn admin-btn-sm" disabled={!craftingPlayerId}
              onClick={() => fetchCraftingPlayer(craftingPlayerId)}>
              🔍 Look Up
            </button>
          </div>

          {craftingPlayerData && (
            <div className="admin-crafting-player">
              <h4>{craftingPlayerData.name}</h4>

              <div className="admin-crafting-disciplines">
                {['smithing', 'alchemy', 'enchanting'].map(disc => {
                  const d = craftingPlayerData.crafting[disc] || { level: 1, xp: 0 };
                  const level = calcLevel(d.xp);
                  const currentXp = xpForLevel(level);
                  const nextXp = xpForLevel(level + 1);
                  const progress = nextXp > currentXp ? Math.round(((d.xp - currentXp) / (nextXp - currentXp)) * 100) : 100;
                  const icons = { smithing: '⚒️', alchemy: '⚗️', enchanting: '🔮' };
                  return (
                    <div key={disc} className="admin-crafting-disc-card">
                      <div className="disc-header">
                        <span className="disc-icon">{icons[disc]}</span>
                        <span className="disc-name">{disc.charAt(0).toUpperCase() + disc.slice(1)}</span>
                        <span className="disc-level">Lv {level}</span>
                      </div>
                      <div className="admin-crafting-bar">
                        <div className="admin-crafting-bar-fill" style={{ width: `${progress}%` }} />
                      </div>
                      <div className="disc-xp-text">{d.xp} / {nextXp} XP ({progress}%)</div>
                    </div>
                  );
                })}
              </div>

              <div className="admin-detail-section" style={{ marginTop: 8 }}>
                <label style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  📜 Known Recipes ({craftingPlayerData.knownRecipes.length}):
                </label>
                <div className="admin-detail-items" style={{ marginTop: 4 }}>
                  {craftingPlayerData.knownRecipes.map(r => (
                    <div key={r} className="admin-item-tag">{r.replace(/_/g, ' ')}</div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="admin-detail-section" style={{ marginTop: 12 }}>
                <h4>Actions</h4>
                <div className="admin-action-row">
                  <select value={craftingDiscipline} onChange={e => setCraftingDiscipline(e.target.value)} className="admin-select admin-select-sm">
                    <option value="smithing">⚒️ Smithing</option>
                    <option value="alchemy">⚗️ Alchemy</option>
                    <option value="enchanting">🔮 Enchanting</option>
                  </select>
                  <input type="number" placeholder="XP amount" value={craftingXpAmount}
                    onChange={e => setCraftingXpAmount(e.target.value)} className="admin-input admin-input-sm" min="1" />
                  <button className="admin-btn admin-btn-sm" disabled={!craftingXpAmount || parseInt(craftingXpAmount) <= 0}
                    onClick={() => { handleCraftingAction('give-xp', craftingPlayerId); setCraftingXpAmount(''); }}>
                    ⚒️ Give XP
                  </button>
                </div>
                <div className="admin-action-row">
                  <select value={craftingDiscipline} onChange={e => setCraftingDiscipline(e.target.value)} className="admin-select admin-select-sm">
                    <option value="smithing">⚒️ Smithing</option>
                    <option value="alchemy">⚗️ Alchemy</option>
                    <option value="enchanting">🔮 Enchanting</option>
                  </select>
                  <input type="number" placeholder="Target level" value={craftingLevelTarget}
                    onChange={e => setCraftingLevelTarget(e.target.value)} className="admin-input admin-input-sm" min="1" max="50" />
                  <button className="admin-btn admin-btn-sm" disabled={!craftingLevelTarget || parseInt(craftingLevelTarget) < 1}
                    onClick={() => { handleCraftingAction('set-level', craftingPlayerId); setCraftingLevelTarget(''); }}>
                    📈 Set Level
                  </button>
                </div>
                <div className="admin-action-row danger-zone">
                  <button className="admin-btn admin-btn-warning" onClick={() => setConfirmAction({
                    message: `Reset ALL crafting data for ${craftingPlayerData.name}? This resets all levels to 1 and clears discovered recipes.`,
                    onConfirm: () => handleCraftingAction('reset', craftingPlayerId)
                  })}>
                    🔄 Reset All Crafting
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────
  //  Lore — admin-only canonical world reference
  //
  //  Renders the docs/lore/*.md files served by GET /api/admin/lore
  //  with a left-pane file list and a right-pane rendered markdown
  //  view. The markdown renderer below is intentionally minimal — it
  //  handles the syntax actually used in the lore docs (headings,
  //  paragraphs, lists, tables, bold/italic, inline code, hr, links)
  //  and avoids pulling in a markdown dependency.
  // ────────────────────────────────────────────────────────────

  const renderLore = () => {
    if (loreLoading && !loreDocs) {
      return <div className="admin-panel"><p className="admin-muted">Loading lore…</p></div>;
    }
    if (!loreDocs) {
      return (
        <div className="admin-panel">
          <p className="admin-muted">Lore not loaded.</p>
          <button className="admin-btn admin-btn-sm" onClick={fetchLore}>Load Lore</button>
        </div>
      );
    }

    const docs = loreDocs.docs || [];
    const search = loreSearch.trim().toLowerCase();
    const filtered = search
      ? docs.filter(d =>
          d.title.toLowerCase().includes(search) ||
          d.content.toLowerCase().includes(search))
      : docs;
    const selected = docs.find(d => d.id === selectedLoreId) || docs[0] || null;

    return (
      <div className="admin-panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>📜 World Lore</h3>
          <span className="admin-muted" style={{ fontSize: 13 }}>
            {docs.length} canonical documents · {(loreDocs.totalBytes / 1024).toFixed(1)} KB
          </span>
          <div style={{ flex: 1 }} />
          <input
            type="text"
            placeholder="Search title or content…"
            value={loreSearch}
            onChange={(e) => setLoreSearch(e.target.value)}
            className="admin-input"
            style={{ minWidth: 240 }}
          />
          <button className="admin-btn admin-btn-sm" onClick={fetchLore} title="Reload from disk">
            🔄 Reload
          </button>
        </div>

        <p className="admin-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>
          Source: <code>docs/lore/*.md</code>. These files are the canonical world reference —
          quest writers, NPC dialogue, and item flavor should align with the names, dates,
          and facts here. Edits are made in the repo, not here. Cache: 60s.
        </p>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Left pane — file list */}
          <div style={{
            flex: '0 0 260px',
            maxHeight: 'calc(100vh - 280px)',
            overflowY: 'auto',
            border: '1px solid #2a2a2a',
            borderRadius: 4,
            background: '#161616',
          }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12 }} className="admin-muted">No matches.</div>
            ) : filtered.map(d => {
              const isActive = selected && selected.id === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedLoreId(d.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: 'none',
                    borderBottom: '1px solid #222',
                    background: isActive ? '#2a3a4a' : 'transparent',
                    color: isActive ? '#fff' : '#ddd',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: isActive ? 600 : 400 }}>{d.title}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    {d.filename} · {(d.sizeBytes / 1024).toFixed(1)} KB
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right pane — rendered markdown */}
          <div style={{
            flex: 1,
            minWidth: 0,
            maxHeight: 'calc(100vh - 280px)',
            overflowY: 'auto',
            border: '1px solid #2a2a2a',
            borderRadius: 4,
            background: '#1a1a1a',
            padding: '20px 28px',
          }}>
            {selected ? (
              <LoreMarkdown source={selected.content} highlight={search} />
            ) : (
              <p className="admin-muted">Select a document.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTools = () => (
    <div className="admin-tools-tab">
      <div className="admin-panel">
        <h3>Account Cleanup</h3>
        <p>Delete all non-admin accounts inactive for the specified number of days (min 7, max 365).</p>
        <div className="admin-action-row">
          <input type="number" value={cleanupDays} onChange={e => setCleanupDays(e.target.value)} className="admin-input admin-input-sm" min="7" max="365" />
          <span>days inactive</span>
          <button className="admin-btn admin-btn-warning" onClick={() => setConfirmAction({
            message: `Delete ALL non-admin accounts that have been inactive for ${cleanupDays}+ days? This cannot be undone!`,
            onConfirm: handleCleanup
          })}>
             Run Cleanup
          </button>
        </div>
      </div>

      <div className="admin-panel">
        <h3>Action Log</h3>
        <div className="admin-action-log">
          {actionLog.length === 0 ? (
            <p className="admin-muted">No actions yet this session</p>
          ) : (
            actionLog.map((entry, i) => (
              <div key={i} className="admin-log-entry">
                <span className="admin-log-time">{entry.time}</span>
                <span className="admin-log-msg">{entry.msg}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  // ============================================================
  // BUG REPORTS TAB
  // ============================================================

  const handleBugResolve = async (id, resolved) => {
    try {
      await axios.patch(`${API}/api/bugs/${id}`, { resolved, adminNotes: bugAdminNotes || undefined });
      logAction(`Bug #${id} ${resolved ? 'resolved' : 'reopened'}`);
      // Navigate back to the list and refresh it
      setSelectedBug(null);
      setBugAdminNotes('');
      fetchBugReports();
    } catch (err) {
      logAction(`Error updating bug: ${err.message}`);
    }
  };

  const handleBugSaveNotes = async (id) => {
    try {
      await axios.patch(`${API}/api/bugs/${id}`, { adminNotes: bugAdminNotes });
      logAction(`Notes saved for bug #${id}`);
      if (selectedBug?.id === id) setSelectedBug(prev => ({ ...prev, admin_notes: bugAdminNotes }));
    } catch (err) {
      logAction(`Error saving notes: ${err.message}`);
    }
  };

  const handleBugDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/bugs/${id}`);
      logAction(`Bug #${id} deleted`);
      setSelectedBug(null);
      fetchBugReports();
    } catch (err) {
      logAction(`Error deleting bug: ${err.message}`);
    }
  };

  const handleBugExport = async (id) => {
    try {
      const res = await axios.get(`${API}/api/bugs/${id}/export`);
      setBugExportText(res.data.exportText);
    } catch (err) {
      logAction(`Error exporting bug: ${err.message}`);
    }
  };

  const handleBugBatchExport = async () => {
    try {
      const res = await axios.get(`${API}/api/bugs/export/batch`);
      setBugExportText(res.data.exportText);
    } catch (err) {
      logAction(`Error batch exporting: ${err.message}`);
    }
  };

  const handleBugViewDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/api/bugs/${id}`);
      setSelectedBug(res.data);
      setBugAdminNotes(res.data.admin_notes || '');
    } catch (err) {
      logAction(`Error fetching bug detail: ${err.message}`);
    }
  };

  const renderPlayerReports = () => {
    const statusColor = (s) => ({
      open: '#ef4444', reviewing: '#f0c060',
      actioned: '#4ade80', dismissed: '#888',
    }[s] || '#888');
    const categoryLabel = (c) => ({
      harassment: 'Harassment', spam: 'Spam', cheating: 'Cheating',
      inappropriate_name: 'Bad name', scam: 'Scam', other: 'Other',
    }[c] || c);

    if (selectedReport) {
      const r = selectedReport;
      const bumps = r.evidence?.bumps || [];
      const chatEvidence = r.evidence?.chat || [];
      return (
        <div>
          <button className="admin-btn" onClick={() => { setSelectedReport(null); setReportChatContext([]); }} style={{ marginBottom: 12 }}>← Back to List</button>
          <div className="admin-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Report #{r.id} — {categoryLabel(r.category)}</h3>
              <span style={{ color: statusColor(r.status), fontWeight: 'bold', textTransform: 'uppercase' }}>{r.status}</span>
            </div>
            <div style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: 12 }}>
              Reporter: <strong style={{ color: '#e0e0e0' }}>{r.reporter_name || 'Unknown'}</strong>
              {' · '}Reported: <strong style={{ color: '#ffaaaa' }}>{r.reported_name || 'Unknown'}</strong>
              {' · '}Created: {new Date(r.created_at).toLocaleString()}
              {r.reviewed_at && <> · Reviewed: {new Date(r.reviewed_at).toLocaleString()}</>}
              {bumps.length > 0 && <> · +{bumps.length} additional submission(s)</>}
            </div>

            {r.description && (
              <div className="admin-panel" style={{ background: '#1a1a2e', marginBottom: 12 }}>
                <h4 style={{ color: '#f0c060', margin: '0 0 8px 0' }}>Reporter Description</h4>
                <p style={{ color: '#e0e0e0', whiteSpace: 'pre-wrap', margin: 0 }}>{r.description}</p>
              </div>
            )}

            {chatEvidence.length > 0 && (
              <div className="admin-panel" style={{ background: '#1a1a10', marginBottom: 12 }}>
                <h4 style={{ color: '#c9a84c', margin: '0 0 8px 0' }}>Evidence Snapshot ({chatEvidence.length} msgs)</h4>
                <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                  {chatEvidence.map((m, i) => (
                    <div key={i} style={{ color: '#bbb', padding: '2px 0' }}>
                      <span style={{ color: '#666' }}>{m.ts ? new Date(m.ts).toLocaleTimeString() : '?'}</span>{' '}
                      <span style={{ color: '#88bbff' }}>[{m.channel || '?'}]</span>{' '}
                      <strong style={{ color: '#e0e0e0' }}>{m.sender || '?'}</strong>: {m.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reportChatContext.length > 0 && (
              <div className="admin-panel" style={{ background: '#0a1a2a', marginBottom: 12 }}>
                <h4 style={{ color: '#88bbff', margin: '0 0 8px 0' }}>Reported Player's Chat (±15m, {reportChatContext.length} msgs)</h4>
                <div style={{ maxHeight: 220, overflowY: 'auto', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                  {reportChatContext.map((m) => (
                    <div key={m.id} style={{ color: '#bbb', padding: '2px 0' }}>
                      <span style={{ color: '#666' }}>{new Date(m.timestamp).toLocaleTimeString()}</span>{' '}
                      <span style={{ color: '#88bbff' }}>[{m.channel}]</span>{' '}
                      <strong style={{ color: '#e0e0e0' }}>{m.sender_name}</strong>: {m.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bumps.length > 0 && (
              <div className="admin-panel" style={{ background: '#2a1a2a', marginBottom: 12 }}>
                <h4 style={{ color: '#f0c0f0', margin: '0 0 8px 0' }}>Additional Submissions ({bumps.length})</h4>
                {bumps.map((b, i) => (
                  <div key={i} style={{ fontSize: '0.85rem', color: '#ccc', padding: '4px 0', borderBottom: i < bumps.length - 1 ? '1px solid #333' : 'none' }}>
                    <div style={{ color: '#888', fontSize: '0.75rem' }}>{new Date(b.at).toLocaleString()}</div>
                    {b.description && <div style={{ whiteSpace: 'pre-wrap' }}>{b.description}</div>}
                  </div>
                ))}
              </div>
            )}

            <div className="admin-panel" style={{ background: '#1a1a2e' }}>
              <h4 style={{ color: '#f0c060', margin: '0 0 8px 0' }}>Moderator Actions</h4>
              <textarea
                value={reportAdminNotes}
                onChange={e => setReportAdminNotes(e.target.value)}
                placeholder="Internal notes (not shown to players)..."
                style={{ width: '100%', background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: 8, minHeight: 60, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={reportAction}
                  onChange={e => setReportAction(e.target.value)}
                  style={{ background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: '6px 8px' }}
                >
                  <option value="none">— Choose action —</option>
                  <option value="warning">⚠️ Warning (system DM)</option>
                  <option value="chat_ban_24h">🔇 Chat ban 24h</option>
                  <option value="chat_ban_7d">🔇 Chat ban 7d</option>
                  <option value="account_ban">⛔ Account ban</option>
                  <option value="dismissed">❌ Dismiss (no action)</option>
                </select>
                <button className="admin-btn" onClick={() => patchReport(r.id, { adminNotes: reportAdminNotes })}>💾 Save Notes</button>
                <button className="admin-btn" onClick={() => patchReport(r.id, { status: 'reviewing' })} disabled={r.status === 'reviewing'}>👁️ Mark Reviewing</button>
                {reportAction !== 'none' && (
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => {
                      if (reportAction === 'dismissed') {
                        if (!window.confirm('Dismiss this report without action?')) return;
                        patchReport(r.id, { status: 'dismissed', action: 'dismissed', adminNotes: reportAdminNotes });
                      } else {
                        const label = reportAction.replace(/_/g, ' ');
                        if (!window.confirm(`Apply "${label}" to ${r.reported_name}?`)) return;
                        patchReport(r.id, { status: 'actioned', action: reportAction, adminNotes: reportAdminNotes });
                      }
                    }}
                  >
                    ✅ Apply action
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    const filters = ['open', 'reviewing', 'actioned', 'dismissed', 'all'];
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>🚩 Player Reports ({reportTotal})</h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {filters.map(f => (
              <button
                key={f}
                className={`admin-btn ${reportFilter === f ? 'admin-btn-success' : ''}`}
                onClick={() => { setReportFilter(f); setReportPage(0); }}
              >
                {f[0].toUpperCase() + f.slice(1)}
                {reportSummary && f !== 'all' && reportSummary[f] > 0 ? ` (${reportSummary[f]})` : ''}
              </button>
            ))}
            <button className="admin-btn" onClick={fetchPlayerReports}>🔄 Refresh</button>
          </div>
        </div>
        {reportSummary && (
          <div style={{ fontSize: '0.82rem', color: '#aaa', marginBottom: 8 }}>
            Open: <b style={{ color: '#ef4444' }}>{reportSummary.open || 0}</b>
            {' · '}Reviewing: <b style={{ color: '#f0c060' }}>{reportSummary.reviewing || 0}</b>
            {' · '}Actioned: <b style={{ color: '#4ade80' }}>{reportSummary.actioned || 0}</b>
            {' · '}Dismissed: <b style={{ color: '#888' }}>{reportSummary.dismissed || 0}</b>
          </div>
        )}
        <div className="admin-panel" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>ID</th><th>Category</th><th>Reporter</th><th>Reported</th>
                <th>Status</th><th>Action</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {playerReports.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#888' }}>No reports.</td></tr>
              )}
              {playerReports.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openReport(r.id)}>
                  <td>#{r.id}</td>
                  <td>{categoryLabel(r.category)}</td>
                  <td>{r.reporter_name || '—'}</td>
                  <td style={{ color: '#ffaaaa' }}>{r.reported_name || '—'}</td>
                  <td><span style={{ color: statusColor(r.status), fontWeight: 'bold' }}>{r.status}</span></td>
                  <td style={{ color: '#aaa', fontSize: '0.85rem' }}>{r.action_taken || '—'}</td>
                  <td style={{ color: '#888', fontSize: '0.82rem' }}>{new Date(r.created_at).toLocaleString()}</td>
                  <td><button className="admin-btn" onClick={(e) => { e.stopPropagation(); openReport(r.id); }}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {reportTotal > 50 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            <button className="admin-btn" onClick={() => setReportPage(p => Math.max(0, p - 1))} disabled={reportPage === 0}>← Prev</button>
            <span style={{ color: '#aaa', padding: '6px 0' }}>Page {reportPage + 1} of {Math.ceil(reportTotal / 50)}</span>
            <button className="admin-btn" onClick={() => setReportPage(p => p + 1)} disabled={(reportPage + 1) * 50 >= reportTotal}>Next →</button>
          </div>
        )}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────
  //  Quest Guide — admin-only reference doc
  // ──────────────────────────────────────────────────────────
  const renderQuestGuide = () => {
    const CAT_COLORS = {
      tutorial:       '#88c0ff',
      main:           '#f0c060',
      good:           '#7cd67c',
      neutral:        '#c0c0c0',
      evil:           '#e07070',
      side:           '#d0a8f0',
      repeatable:     '#70d0d0',
      advanced_class: '#f080c0',
    };
    const catColor = (c) => CAT_COLORS[c] || '#aaa';

    // v600: Bridge quests added in 2025 pacing pass
    const NEW_IN_V600 = new Set([
      'good.quest4', 'good.quest7', 'good.quest10', 'good.quest13', 'good.quest16',
      'neutral.quest5', 'neutral.quest8', 'neutral.quest11', 'neutral.quest14', 'neutral.quest17',
      'evil.quest6', 'evil.quest9', 'evil.quest12', 'evil.quest15', 'evil.quest18',
    ]);
    const v600Badge = () => (
      <span
        title="New in v600 — bridge quest added to fill a level gap"
        style={{
          display: 'inline-block',
          padding: '1px 6px',
          borderRadius: 8,
          background: '#f0a030',
          color: '#111',
          fontSize: '0.62rem',
          fontWeight: 800,
          letterSpacing: 0.3,
          marginLeft: 4,
        }}
      >v600</span>
    );

    // v601: Act 3 bridge quests (L36/43/50/55/58)
    const NEW_IN_V601 = new Set([
      'good.quest19', 'good.quest22', 'good.quest25', 'good.quest28', 'good.quest29',
      'neutral.quest20', 'neutral.quest23', 'neutral.quest26', 'neutral.quest29', 'neutral.quest30',
      'evil.quest21', 'evil.quest24', 'evil.quest27', 'evil.quest30', 'evil.quest31',
    ]);
    const v601Badge = () => (
      <span
        title="New in v601 — Act 3 bridge quest added to fill a level gap"
        style={{
          display: 'inline-block',
          padding: '1px 6px',
          borderRadius: 8,
          background: '#a060f0',
          color: '#fff',
          fontSize: '0.62rem',
          fontWeight: 800,
          letterSpacing: 0.3,
          marginLeft: 4,
        }}
      >v601</span>
    );

    // Flatten all quests once for lookup + filtering
    const allQuests = questGuide
      ? questGuide.categories.flatMap(c => c.quests)
      : [];
    const questByPath = new Map(allQuests.map(q => [q.varPath, q]));
    const selected = selectedQuestPath ? questByPath.get(selectedQuestPath) : null;

    const searchLower = questGuideSearch.trim().toLowerCase();
    const matchesFilter = (q) => {
      if (questGuideCategory !== 'all' && q.category !== questGuideCategory) return false;
      if (!searchLower) return true;
      return (
        (q.name || '').toLowerCase().includes(searchLower) ||
        (q.giver || '').toLowerCase().includes(searchLower) ||
        (q.description || '').toLowerCase().includes(searchLower) ||
        (q.varPath || '').toLowerCase().includes(searchLower)
      );
    };

    const filteredCategories = questGuide
      ? questGuide.categories
          .map(c => ({ ...c, quests: c.quests.filter(matchesFilter) }))
          .filter(c => c.quests.length > 0)
      : [];

    const filteredChains = questGuide
      ? questGuide.chains.filter(ch =>
          ch.quests.some(q => matchesFilter(questByPath.get(q.varPath) || q))
        )
      : [];

    // Global cross-chain edges (for chains-view annotations)
    const edgesByFromChain = new Map();
    if (questGuide && Array.isArray(questGuide.crossChainEdges)) {
      for (const e of questGuide.crossChainEdges) {
        const fromChainKey = e.from.split('.')[0];
        if (!edgesByFromChain.has(fromChainKey)) edgesByFromChain.set(fromChainKey, []);
        edgesByFromChain.get(fromChainKey).push(e);
      }
    }

    const categoryPill = (cat) => (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 10,
          background: catColor(cat),
          color: '#111',
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        {cat.replace('_', ' ')}
      </span>
    );

    const questListItem = (q) => (
      <div
        key={q.varPath}
        onClick={() => setSelectedQuestPath(q.varPath)}
        style={{
          padding: '6px 8px',
          margin: '2px 0',
          cursor: 'pointer',
          borderRadius: 4,
          background: selectedQuestPath === q.varPath ? 'rgba(240,192,96,0.18)' : 'transparent',
          borderLeft: `3px solid ${catColor(q.category)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {q.name}
            {NEW_IN_V600.has(q.varPath) && v600Badge()}
            {NEW_IN_V601.has(q.varPath) && v601Badge()}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {q.giver || '—'} · <code>{q.varPath}</code>
          </div>
        </div>
        {q.levelReq > 0 && (
          <span style={{ fontSize: '0.7rem', color: '#aaa', whiteSpace: 'nowrap' }}>Lv {q.levelReq}</span>
        )}
      </div>
    );

    const renderListView = () => (
      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, alignItems: 'start' }}>
        {/* Left: categories */}
        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>
          {filteredCategories.length === 0 && (
            <p className="admin-muted">No quests match the current filters.</p>
          )}
          {filteredCategories.map(cat => (
            <div key={cat.key} style={{ marginBottom: 14 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderBottom: `1px solid ${catColor(cat.key)}44`,
                paddingBottom: 4,
                marginBottom: 4,
              }}>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%', background: catColor(cat.key),
                  display: 'inline-block',
                }} />
                <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{cat.label}</h4>
                <span className="admin-muted" style={{ fontSize: '0.78rem' }}>
                  ({cat.quests.length})
                </span>
              </div>
              {cat.quests.map(questListItem)}
            </div>
          ))}
        </div>

        {/* Right: detail */}
        <div style={{ minHeight: 300 }}>
          {!selected ? (
            <div className="admin-muted" style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: 6 }}>📖</div>
              Select a quest to view its details, stages, and chain links.
            </div>
          ) : (
            renderQuestDetail(selected)
          )}
        </div>
      </div>
    );

    const renderChainsView = () => (
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {filteredChains.length === 0 && (
          <p className="admin-muted">No chains match the current filters.</p>
        )}
        {filteredChains.map(ch => (
          <div key={ch.chainKey} style={{
            marginBottom: 14,
            padding: 10,
            border: `1px solid ${catColor(ch.category)}44`,
            borderRadius: 6,
            background: 'rgba(255,255,255,0.02)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {categoryPill(ch.category)}
              <strong style={{ fontSize: '0.9rem' }}>
                {ch.chainKey === ch.category ? `${ch.category} main chain` : ch.chainKey}
              </strong>
              <span className="admin-muted" style={{ fontSize: '0.78rem' }}>
                {ch.length} quest{ch.length === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
              {ch.quests.map((q, idx) => {
                const prev = idx > 0 ? ch.quests[idx - 1] : null;
                const delta = prev && prev.levelReq > 0 && q.levelReq > 0
                  ? q.levelReq - prev.levelReq
                  : null;
                const bigGap = delta !== null && delta >= 5;
                return (
                <React.Fragment key={q.varPath}>
                  <button
                    onClick={() => setSelectedQuestPath(q.varPath)}
                    style={{
                      background: selectedQuestPath === q.varPath ? catColor(ch.category) : '#222',
                      color: selectedQuestPath === q.varPath ? '#111' : '#ddd',
                      border: `1px solid ${catColor(ch.category)}88`,
                      borderRadius: 4,
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                      textAlign: 'left',
                      minWidth: 160,
                      maxWidth: 240,
                    }}
                    title={`${q.giver || ''} · ${q.varPath}${delta !== null ? ` · Δ${delta} levels from prior` : ''}`}
                  >
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {q.name}
                      {NEW_IN_V600.has(q.varPath) && v600Badge()}
                      {NEW_IN_V601.has(q.varPath) && v601Badge()}
                    </div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.75 }}>
                      {q.levelReq > 0 ? `Lv ${q.levelReq}` : 'Lv —'}
                      {delta !== null && (
                        <span style={{ color: bigGap ? '#ff8888' : '#8a8', marginLeft: 4 }}>
                          {bigGap ? '⚠' : ''} (+{delta})
                        </span>
                      )}
                      {' · '}{q.giver || ''}
                    </div>
                  </button>
                  {idx < ch.quests.length - 1 && (
                    <span style={{ color: catColor(ch.category), fontSize: '1.1rem' }}>→</span>
                  )}
                </React.Fragment>
                );
              })}
            </div>

            {(() => {
              // Cross-chain edges departing this chain (keyed on chainKey
              // which for good/neutral/evil equals the category). Only show
              // when there are edges leaving THIS chainKey.
              const edges = edgesByFromChain.get(ch.chainKey) || [];
              if (edges.length === 0) return null;
              return (
                <div style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: `1px dashed ${catColor(ch.category)}33`,
                  fontSize: '0.76rem',
                }}>
                  <div style={{ color: '#888', marginBottom: 4 }}>
                    Cross-chain routes from this chain ({edges.length}):
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {edges.map((e, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setQuestGuideView('list');
                          setSelectedQuestPath(e.to);
                        }}
                        style={{
                          padding: '2px 6px',
                          background: e.lockoutCurrent ? '#3a1414' : '#14223a',
                          border: `1px solid ${e.lockoutCurrent ? '#6a2a2a' : '#2a3a6a'}`,
                          color: e.lockoutCurrent ? '#ff8888' : '#88bbff',
                          borderRadius: 3,
                          cursor: 'pointer',
                          fontSize: '0.72rem',
                        }}
                        title={`via ${e.outcomeLabel}${e.lockoutCurrent ? ' — locks out current chain' : ''}`}
                      >
                        <strong>{e.fromName || e.from}</strong> → {e.toName || e.to}
                        {e.lockoutCurrent && ' 🔒'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ))}

        {selected && (
          <div style={{ marginTop: 16 }}>
            {renderQuestDetail(selected)}
          </div>
        )}
      </div>
    );

    // DAG view — 3-column vertical graph of the good/neutral/evil chains
    // with SVG-drawn cross-chain curves. Tutorial/side/AC/thormir chains
    // have no cross-chain edges so they stay in list/chains views.
    const renderQuestDAG = () => {
      if (viewportW < 1100) {
        return (
          <div className="admin-muted" style={{ padding: 16, fontSize: '0.88rem' }}>
            Graph view needs at least 1100px of horizontal space to render the
            three alignment columns cleanly. Resize the window or switch back
            to List / Chains view.
          </div>
        );
      }

      const DAG_CHAINS = ['good', 'neutral', 'evil'];
      const CHAIN_LABELS = {
        good:    'Path of Good',
        neutral: 'Path of Neutrality',
        evil:    'Path of Evil',
      };

      // Collect quests per chain, sorted by levelReq then varPath
      const byCol = {};
      for (const ck of DAG_CHAINS) byCol[ck] = [];
      for (const q of allQuests) {
        if (DAG_CHAINS.includes(q.chainKey)) byCol[q.chainKey].push(q);
      }
      for (const ck of DAG_CHAINS) {
        byCol[ck].sort(
          (a, b) => (a.levelReq - b.levelReq) || a.varPath.localeCompare(b.varPath)
        );
      }

      // Layout constants
      const NODE_W = 190;
      const NODE_H = 52;
      const ROW_GAP = 74;
      const COL_GAP = 280;
      const LEFT_PAD = 40;
      const TOP_PAD = 70;
      const HEADER_Y = 20;

      const positions = new Map(); // varPath -> {x, y, cx, cy, col, ck}
      for (let col = 0; col < DAG_CHAINS.length; col++) {
        const ck = DAG_CHAINS[col];
        byCol[ck].forEach((q, row) => {
          const x = LEFT_PAD + col * COL_GAP;
          const y = TOP_PAD + row * ROW_GAP;
          positions.set(q.varPath, {
            x, y,
            cx: x + NODE_W / 2,
            cy: y + NODE_H / 2,
            col,
            ck,
          });
        });
      }

      const maxRows = Math.max(...DAG_CHAINS.map((ck) => byCol[ck].length));
      const svgW = LEFT_PAD * 2 + DAG_CHAINS.length * COL_GAP - (COL_GAP - NODE_W);
      const svgH = TOP_PAD + maxRows * ROW_GAP + 20;

      // Cross-chain edges — drop self-loops and endpoints not in DAG
      const crossEdges = (questGuide.crossChainEdges || []).filter((e) => {
        const a = positions.get(e.from);
        const b = positions.get(e.to);
        return a && b && a.ck !== b.ck;
      });

      // Per-source lockout markers: a quest has at least one outcome that
      // locks out its own chain (setVar own -50). Counted from any edge
      // whose lockoutCurrent is true.
      const lockoutSet = new Set();
      for (const e of crossEdges) {
        if (e.lockoutCurrent) lockoutSet.add(e.from);
      }

      // Advance edges — same-chain adjacent pairs (vertical spine)
      const advanceEdges = [];
      for (const ck of DAG_CHAINS) {
        const list = byCol[ck];
        for (let i = 0; i < list.length - 1; i++) {
          advanceEdges.push({ from: list[i].varPath, to: list[i + 1].varPath });
        }
      }

      // Stagger edges that share a source node to avoid overlap
      const sourceCounts = new Map();
      const edgeStaggerIndex = crossEdges.map((e) => {
        const n = sourceCounts.get(e.from) || 0;
        sourceCounts.set(e.from, n + 1);
        return n;
      });

      // Build path for a cross-chain cubic Bézier between two nodes
      const curvePath = (fromPos, toPos, staggerIdx) => {
        const leftward = toPos.col < fromPos.col;
        const sx = leftward ? fromPos.x : fromPos.x + NODE_W;
        const sy = fromPos.cy + (staggerIdx - 1) * 6;
        const tx = leftward ? toPos.x + NODE_W : toPos.x;
        const ty = toPos.cy;
        const dx = tx - sx;
        const c1x = sx + dx * 0.5;
        const c1y = sy;
        const c2x = tx - dx * 0.5;
        const c2y = ty;
        return `M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${tx} ${ty}`;
      };

      const isSelected = (varPath) => varPath === selectedQuestPath;
      const edgeTouchesSelected = (e) =>
        !!selectedQuestPath && (e.from === selectedQuestPath || e.to === selectedQuestPath);
      const hasSelection = !!selectedQuestPath && positions.has(selectedQuestPath);

      // Legend swatch helper
      const legend = (color, label, dashed = false) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
          <svg width="24" height="10">
            <line
              x1="0" y1="5" x2="24" y2="5"
              stroke={color}
              strokeWidth="2"
              strokeDasharray={dashed ? '4 2' : undefined}
            />
          </svg>
          {label}
        </span>
      );

      return (
        <div>
          {/* Toolbar */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14,
            padding: '8px 10px', marginBottom: 8,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid #333',
            borderRadius: 4,
          }}>
            {legend('#6a8a6a', 'same-chain advance')}
            {legend('#88bbff', 'cross-chain open')}
            {legend('#e07070', 'lockout cross-chain', true)}
            <span style={{ fontSize: '0.78rem' }}>🔒 = outcome also locks out current chain</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
              <label style={{ fontSize: '0.78rem', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={dagShowAdvances} onChange={(e) => setDagShowAdvances(e.target.checked)} />
                advances
              </label>
              <label style={{ fontSize: '0.78rem', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={dagShowCrossChain} onChange={(e) => setDagShowCrossChain(e.target.checked)} />
                cross-chain
              </label>
              <label style={{ fontSize: '0.78rem', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={dagShowLockouts} onChange={(e) => setDagShowLockouts(e.target.checked)} />
                lockouts
              </label>
              <div style={{ display: 'inline-flex', gap: 4 }}>
                <button className="admin-btn admin-btn-sm" onClick={() => setDagZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}>−</button>
                <span style={{ fontSize: '0.78rem', minWidth: 42, textAlign: 'center' }}>{Math.round(dagZoom * 100)}%</span>
                <button className="admin-btn admin-btn-sm" onClick={() => setDagZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))}>+</button>
                <button className="admin-btn admin-btn-sm" onClick={() => setDagZoom(1)}>Reset</button>
              </div>
            </div>
          </div>

          {/* Scrollable SVG container */}
          <div style={{
            maxHeight: '70vh',
            overflow: 'auto',
            border: '1px solid #222',
            borderRadius: 4,
            background: '#0d0d12',
          }}>
            <svg
              width={svgW * dagZoom}
              height={svgH * dagZoom}
              viewBox={`0 0 ${svgW} ${svgH}`}
              style={{ display: 'block' }}
            >
              <defs>
                <marker id="arrow-cross" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill="#88bbff" />
                </marker>
                <marker id="arrow-lockout" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill="#e07070" />
                </marker>
                <marker id="arrow-advance" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill="#6a8a6a" />
                </marker>
              </defs>

              {/* Column bands */}
              {DAG_CHAINS.map((ck, col) => (
                <rect
                  key={`band-${ck}`}
                  x={LEFT_PAD + col * COL_GAP - 14}
                  y={HEADER_Y + 28}
                  width={NODE_W + 28}
                  height={svgH - HEADER_Y - 32}
                  fill={catColor(ck)}
                  opacity="0.04"
                  rx="6"
                />
              ))}

              {/* Column headers */}
              {DAG_CHAINS.map((ck, col) => (
                <g key={`hdr-${ck}`}>
                  <rect
                    x={LEFT_PAD + col * COL_GAP}
                    y={HEADER_Y}
                    width={NODE_W}
                    height={30}
                    rx="4"
                    fill={catColor(ck)}
                    opacity="0.85"
                  />
                  <text
                    x={LEFT_PAD + col * COL_GAP + NODE_W / 2}
                    y={HEADER_Y + 20}
                    textAnchor="middle"
                    fontSize="13"
                    fontWeight="700"
                    fill="#111"
                  >
                    {CHAIN_LABELS[ck]} ({byCol[ck].length})
                  </text>
                </g>
              ))}

              {/* Advance edges (vertical spines) */}
              {dagShowAdvances && advanceEdges.map((e, i) => {
                const a = positions.get(e.from);
                const b = positions.get(e.to);
                if (!a || !b) return null;
                const dim = hasSelection && e.from !== selectedQuestPath && e.to !== selectedQuestPath;
                return (
                  <line
                    key={`adv-${i}`}
                    x1={a.cx} y1={a.y + NODE_H}
                    x2={b.cx} y2={b.y}
                    stroke="#6a8a6a"
                    strokeWidth="1.5"
                    opacity={dim ? 0.2 : 0.7}
                    markerEnd="url(#arrow-advance)"
                  />
                );
              })}

              {/* Cross-chain edges */}
              {crossEdges.map((e, i) => {
                const a = positions.get(e.from);
                const b = positions.get(e.to);
                if (!a || !b) return null;
                if (e.lockoutCurrent && !dagShowLockouts) return null;
                if (!e.lockoutCurrent && !dagShowCrossChain) return null;
                const dim = hasSelection && !edgeTouchesSelected(e);
                const color = e.lockoutCurrent ? '#e07070' : '#88bbff';
                const marker = e.lockoutCurrent ? 'url(#arrow-lockout)' : 'url(#arrow-cross)';
                return (
                  <g key={`cx-${i}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedQuestPath(e.to)}>
                    <path
                      d={curvePath(a, b, edgeStaggerIndex[i])}
                      fill="none"
                      stroke={color}
                      strokeWidth="1.8"
                      strokeDasharray={e.lockoutCurrent ? '4 3' : undefined}
                      opacity={dim ? 0.15 : 0.85}
                      markerEnd={marker}
                    >
                      <title>{`${e.fromName || e.from}  →  ${e.toName || e.to}\nvia ${e.outcomeLabel}${e.lockoutCurrent ? ' (locks out current chain)' : ''}`}</title>
                    </path>
                  </g>
                );
              })}

              {/* Nodes */}
              {DAG_CHAINS.flatMap((ck) =>
                byCol[ck].map((q) => {
                  const p = positions.get(q.varPath);
                  if (!p) return null;
                  const sel = isSelected(q.varPath);
                  const dim = hasSelection && !sel;
                  const col = catColor(q.chainKey);
                  const hasLockout = lockoutSet.has(q.varPath);
                  return (
                    <g
                      key={q.varPath}
                      transform={`translate(${p.x}, ${p.y})`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedQuestPath(q.varPath)}
                      opacity={dim ? 0.35 : 1}
                    >
                      <rect
                        width={NODE_W}
                        height={NODE_H}
                        rx="5"
                        fill={sel ? col : '#1a1a20'}
                        stroke={col}
                        strokeWidth={sel ? 2.5 : 1.2}
                        style={sel ? { filter: `drop-shadow(0 0 6px ${col})` } : undefined}
                      />
                      <text
                        x="10"
                        y="20"
                        fontSize="11.5"
                        fontWeight="600"
                        fill={sel ? '#111' : '#e0e0e0'}
                      >
                        {(q.name || q.varPath).slice(0, 26)}
                      </text>
                      <text
                        x="10"
                        y="38"
                        fontSize="10"
                        fill={sel ? '#222' : '#8a8a8a'}
                      >
                        Lv {q.levelReq || '—'} · {(q.giver || '').slice(0, 22)}
                      </text>
                      {hasLockout && (
                        <text
                          x={NODE_W - 8}
                          y="16"
                          textAnchor="end"
                          fontSize="12"
                        >
                          <title>This quest has an outcome that locks out its own chain (setVar … -50)</title>
                          🔒
                        </text>
                      )}
                      {NEW_IN_V600.has(q.varPath) && (
                        <g>
                          <rect
                            x={NODE_W - 44}
                            y={NODE_H - 16}
                            width="36"
                            height="12"
                            rx="2"
                            fill="#f0a030"
                          />
                          <text
                            x={NODE_W - 26}
                            y={NODE_H - 7}
                            textAnchor="middle"
                            fontSize="9"
                            fontWeight="700"
                            fill="#111"
                          >
                            <title>New in v600 — bridge quest filling a level gap</title>
                            v600
                          </text>
                        </g>
                      )}
                      {NEW_IN_V601.has(q.varPath) && (
                        <g>
                          <rect
                            x={NODE_W - 44}
                            y={NODE_H - 16}
                            width="36"
                            height="12"
                            rx="2"
                            fill="#a060f0"
                          />
                          <text
                            x={NODE_W - 26}
                            y={NODE_H - 7}
                            textAnchor="middle"
                            fontSize="9"
                            fontWeight="700"
                            fill="#fff"
                          >
                            <title>New in v601 — Act 3 bridge quest filling a level gap</title>
                            v601
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })
              )}
            </svg>
          </div>

          {/* Detail panel below graph when a quest is selected */}
          {selected && DAG_CHAINS.includes(selected.chainKey) && (
            <div style={{ marginTop: 14 }}>
              {renderQuestDetail(selected)}
            </div>
          )}
          {selected && !DAG_CHAINS.includes(selected.chainKey) && (
            <div className="admin-muted" style={{
              marginTop: 14, padding: 10,
              background: 'rgba(240,192,96,0.06)',
              border: '1px solid rgba(240,192,96,0.25)',
              borderRadius: 4,
              fontSize: '0.85rem',
            }}>
              Selected quest <code>{selected.varPath}</code> is in the <strong>{selected.category}</strong> category,
              which is not shown in the DAG view. Switch to List or Chains view to see its details.
            </div>
          )}
        </div>
      );
    };

    // Pacing view — Gantt-style timeline across L1–60 per alignment chain.
    // Surfaces level spacing, highlights v600 bridge quests, and calls out
    // any remaining Δ≥5 gaps with a red tick.
    const renderQuestPacing = () => {
      const PACE_CHAINS = ['good', 'neutral', 'evil'];
      const CHAIN_LABELS = {
        good:    'Path of Good',
        neutral: 'Path of Neutrality',
        evil:    'Path of Evil',
      };
      const MAX_LEVEL = 60;

      const chainQuests = {};
      for (const ck of PACE_CHAINS) chainQuests[ck] = [];
      for (const q of allQuests) {
        if (PACE_CHAINS.includes(q.chainKey) && q.levelReq > 0) {
          chainQuests[q.chainKey].push(q);
        }
      }
      for (const ck of PACE_CHAINS) {
        chainQuests[ck].sort((a, b) => (a.levelReq - b.levelReq) || a.varPath.localeCompare(b.varPath));
      }

      const TRACK_H = 76;
      const TRACK_PAD = 18;
      const LABEL_W = 140;
      const TIMELINE_W = 880;
      const TOTAL_W = LABEL_W + TIMELINE_W + 30;
      const TOTAL_H = 60 + PACE_CHAINS.length * (TRACK_H + 14) + 20;
      const xForLevel = (lv) => LABEL_W + (lv / MAX_LEVEL) * TIMELINE_W;

      // Act band backgrounds
      const acts = [
        { name: 'Act 1', lo: 1,  hi: 20, color: '#7cd67c' },
        { name: 'Act 2', lo: 20, hi: 40, color: '#f0c060' },
        { name: 'Act 3', lo: 40, hi: 60, color: '#e07070' },
      ];

      const stats = PACE_CHAINS.map((ck) => {
        const qs = chainQuests[ck];
        const deltas = [];
        for (let i = 1; i < qs.length; i++) deltas.push(qs[i].levelReq - qs[i - 1].levelReq);
        const maxGap = deltas.length ? Math.max(...deltas) : 0;
        const bigGaps = deltas.filter((d) => d >= 5).length;
        return { ck, count: qs.length, maxGap, bigGaps };
      });

      return (
        <div>
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14,
            padding: '8px 10px', marginBottom: 8,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid #333', borderRadius: 4,
            fontSize: '0.82rem',
          }}>
            {stats.map((s) => (
              <span key={s.ck} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: catColor(s.ck) }} />
                <strong>{CHAIN_LABELS[s.ck]}</strong>
                <span className="admin-muted">
                  {s.count} quests · max Δ {s.maxGap}
                  {s.bigGaps > 0 ? ` · ${s.bigGaps} gap${s.bigGaps === 1 ? '' : 's'} ≥5` : ' · pacing OK'}
                </span>
              </span>
            ))}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, background: '#f0a030', borderRadius: 2 }} />
                v600 bridge
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, background: '#ff8888', borderRadius: 2 }} />
                Δ≥5 gap
              </span>
            </span>
          </div>

          <div style={{ maxHeight: '70vh', overflow: 'auto', border: '1px solid #222', borderRadius: 4, background: '#0d0d12' }}>
            <svg width={TOTAL_W} height={TOTAL_H} style={{ display: 'block' }}>
              {/* Act bands */}
              {acts.map((a, i) => (
                <g key={`act-${i}`}>
                  <rect
                    x={xForLevel(a.lo)} y={30}
                    width={xForLevel(a.hi) - xForLevel(a.lo)}
                    height={TOTAL_H - 40}
                    fill={a.color} opacity="0.05"
                  />
                  <text
                    x={(xForLevel(a.lo) + xForLevel(a.hi)) / 2} y={22}
                    textAnchor="middle" fontSize="11" fontWeight="700" fill={a.color}
                  >
                    {a.name} (L{a.lo}–{a.hi})
                  </text>
                </g>
              ))}

              {/* Level axis ticks (every 5) */}
              {Array.from({ length: MAX_LEVEL / 5 + 1 }, (_, i) => i * 5).map((lv) => (
                <g key={`tick-${lv}`}>
                  <line x1={xForLevel(lv)} y1={30} x2={xForLevel(lv)} y2={TOTAL_H - 10} stroke="#222" strokeWidth="1" />
                  <text x={xForLevel(lv)} y={TOTAL_H - 2} textAnchor="middle" fontSize="9" fill="#666">
                    L{lv}
                  </text>
                </g>
              ))}

              {/* Chain tracks */}
              {PACE_CHAINS.map((ck, rowIdx) => {
                const yTop = 44 + rowIdx * (TRACK_H + 14);
                const cy = yTop + TRACK_H / 2;
                const qs = chainQuests[ck];
                return (
                  <g key={`track-${ck}`}>
                    {/* Label */}
                    <text x={8} y={cy - 4} fontSize="13" fontWeight="700" fill={catColor(ck)}>
                      {CHAIN_LABELS[ck]}
                    </text>
                    <text x={8} y={cy + 14} fontSize="10" fill="#888">
                      {qs.length} quests
                    </text>

                    {/* Spine */}
                    <line
                      x1={xForLevel(1)} y1={cy} x2={xForLevel(MAX_LEVEL)} y2={cy}
                      stroke={catColor(ck)} strokeWidth="2" opacity="0.35"
                    />

                    {/* Gap segments — red when Δ≥5 */}
                    {qs.map((q, i) => {
                      if (i === 0) return null;
                      const prev = qs[i - 1];
                      const delta = q.levelReq - prev.levelReq;
                      if (delta < 5) return null;
                      return (
                        <line
                          key={`gap-${i}`}
                          x1={xForLevel(prev.levelReq)} y1={cy}
                          x2={xForLevel(q.levelReq)} y2={cy}
                          stroke="#ff8888" strokeWidth="3" opacity="0.6"
                        />
                      );
                    })}

                    {/* Quest dots */}
                    {qs.map((q) => {
                      const isV600 = NEW_IN_V600.has(q.varPath);
                      const isV601 = NEW_IN_V601.has(q.varPath);
                      const isNew = isV600 || isV601;
                      const sel = selectedQuestPath === q.varPath;
                      const cx = xForLevel(q.levelReq);
                      const r = isNew ? 7 : 6;
                      const dotFill = isV601 ? '#a060f0' : isV600 ? '#f0a030' : catColor(ck);
                      const newSuffix = isV600 ? ' · NEW IN v600' : isV601 ? ' · NEW IN v601' : '';
                      return (
                        <g key={q.varPath} style={{ cursor: 'pointer' }} onClick={() => setSelectedQuestPath(q.varPath)}>
                          <circle
                            cx={cx} cy={cy} r={r}
                            fill={dotFill}
                            stroke={sel ? '#fff' : '#111'}
                            strokeWidth={sel ? 2.5 : 1}
                          >
                            <title>{`${q.name} · Lv ${q.levelReq} · ${q.varPath}${newSuffix}`}</title>
                          </circle>
                          <text
                            x={cx} y={cy - r - 3}
                            textAnchor="middle" fontSize="9" fill={sel ? '#fff' : '#aaa'}
                          >
                            L{q.levelReq}
                          </text>
                          {sel && (
                            <text x={cx} y={cy + r + 11} textAnchor="middle" fontSize="9" fill="#f0c060">
                              {(q.name || q.varPath).slice(0, 22)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </g>
                );
              })}
            </svg>
          </div>

          {selected && PACE_CHAINS.includes(selected.chainKey) && (
            <div style={{ marginTop: 14 }}>
              {renderQuestDetail(selected)}
            </div>
          )}
        </div>
      );
    };

    if (questGuideLoading && !questGuide) {
      return <div className="admin-panel"><p className="admin-muted">Loading quest guide…</p></div>;
    }
    if (!questGuide) {
      return (
        <div className="admin-panel">
          <button className="admin-btn admin-btn-sm" onClick={fetchQuestGuide}>Load Quest Guide</button>
        </div>
      );
    }

    const s = questGuide.stats;
    return (
      <div className="admin-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>📖 Quest Guide</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={`admin-btn admin-btn-sm ${questGuideView === 'list' ? '' : ''}`}
              onClick={() => setQuestGuideView('list')}
              style={{ background: questGuideView === 'list' ? '#f0c060' : undefined, color: questGuideView === 'list' ? '#111' : undefined }}
            >📋 List</button>
            <button
              className="admin-btn admin-btn-sm"
              onClick={() => setQuestGuideView('chains')}
              style={{ background: questGuideView === 'chains' ? '#f0c060' : undefined, color: questGuideView === 'chains' ? '#111' : undefined }}
            >🔗 Chains</button>
            {viewportW >= 1100 && (
              <button
                className="admin-btn admin-btn-sm"
                onClick={() => setQuestGuideView('graph')}
                style={{ background: questGuideView === 'graph' ? '#f0c060' : undefined, color: questGuideView === 'graph' ? '#111' : undefined }}
                title="DAG view covers the Good/Neutral/Evil alignment chains (30 quests, 36 cross-chain edges)"
              >🕸 Graph</button>
            )}
            <button
              className="admin-btn admin-btn-sm"
              onClick={() => setQuestGuideView('pacing')}
              style={{ background: questGuideView === 'pacing' ? '#f0c060' : undefined, color: questGuideView === 'pacing' ? '#111' : undefined }}
              title="Level-pacing timeline across L1–60 for each alignment chain"
            >📊 Pacing</button>
            <button className="admin-btn admin-btn-sm" onClick={fetchQuestGuide}>🔄 Refresh</button>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, fontSize: '0.82rem', color: '#aaa' }}>
          <span><strong style={{ color: '#ddd' }}>{s.total}</strong> quests</span>
          <span><strong style={{ color: '#ddd' }}>{s.chains}</strong> chains</span>
          <span><strong style={{ color: '#ddd' }}>{s.uniqueGivers}</strong> unique givers</span>
          <span><strong style={{ color: '#ddd' }}>{s.withStages}</strong> with stages</span>
          {s.topology && (
            <>
              <span style={{ color: '#88bbff' }}>
                <strong style={{ color: '#ddd' }}>{s.topology.crossChainEdgeCount}</strong> cross-chain routes
              </span>
              <span style={{ color: '#ff8888' }}>
                <strong style={{ color: '#ddd' }}>{s.topology.lockoutOutcomeCount}</strong> lockout outcomes
              </span>
            </>
          )}
          {Object.entries(s.byCategory).map(([k, n]) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: catColor(k) }} />
              {k}: <strong style={{ color: '#ddd' }}>{n}</strong>
            </span>
          ))}
        </div>

        <div style={{
          marginTop: 10,
          padding: '8px 12px',
          background: 'rgba(136,187,255,0.08)',
          border: '1px solid rgba(136,187,255,0.25)',
          borderRadius: 4,
          fontSize: '0.8rem',
          color: '#ccd',
        }}>
          ℹ️ <strong>Chains are soft-separated.</strong> The Good/Neutral/Evil lines are
          <em> not</em> mutually exclusive — mid-to-late quests have alternate outcomes
          that can lock out the current chain (<code style={{ color: '#ff8888' }}>setVar … -50</code>) and
          simultaneously open a slot in an opposing alignment's chain (blue <code>→</code> edges below).
          A player can hold quests in all three chains at once. See each quest's
          <strong> Branching Outcomes</strong> section for its specific routes.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10, marginBottom: 12, alignItems: 'center' }}>
          <label style={{ fontSize: '0.85rem' }}>
            Category:{' '}
            <select
              value={questGuideCategory}
              onChange={(e) => setQuestGuideCategory(e.target.value)}
            >
              <option value="all">All</option>
              {questGuide.categories.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: '0.85rem' }}>
            Search:{' '}
            <input
              type="text"
              value={questGuideSearch}
              placeholder="name / giver / description / varPath"
              onChange={(e) => setQuestGuideSearch(e.target.value)}
              style={{ width: 260 }}
            />
          </label>
          {(questGuideCategory !== 'all' || questGuideSearch) && (
            <button
              className="admin-btn admin-btn-sm"
              onClick={() => { setQuestGuideCategory('all'); setQuestGuideSearch(''); }}
            >Clear</button>
          )}
        </div>

        {questGuideView === 'chains' ? renderChainsView() : questGuideView === 'graph' ? renderQuestDAG() : questGuideView === 'pacing' ? renderQuestPacing() : renderListView()}
      </div>
    );
  };

  // Quest detail panel (used by both views of the Quest Guide)
  const renderQuestDetail = (q) => {
    const CAT_COLORS = {
      tutorial:       '#88c0ff',
      main:           '#f0c060',
      good:           '#7cd67c',
      neutral:        '#c0c0c0',
      evil:           '#e07070',
      side:           '#d0a8f0',
      repeatable:     '#70d0d0',
      advanced_class: '#f080c0',
    };
    const color = CAT_COLORS[q.category] || '#aaa';
    const chip = (label, value) => (
      <div style={{
        background: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: 4,
        padding: '4px 8px',
        fontSize: '0.78rem',
      }}>
        <div style={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </div>
        <div style={{ color: '#ddd', wordBreak: 'break-word' }}>{value || '—'}</div>
      </div>
    );

    const destinationLabel = Array.isArray(q.destinationArea)
      ? q.destinationArea.map(d => d && d.area ? `${d.area} (state ≤ ${d.maxVal})` : String(d)).join(' → ')
      : (q.destinationArea || '—');

    const npcIdLabel = Array.isArray(q.npcId) ? q.npcId.join(', ') : (q.npcId || '—');

    return (
      <div style={{
        border: `1px solid ${color}55`,
        borderRadius: 6,
        padding: 14,
        background: 'rgba(255,255,255,0.02)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{
                padding: '2px 8px', borderRadius: 10, background: color, color: '#111',
                fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
              }}>
                {q.category.replace('_', ' ')}
              </span>
              {q.levelReq > 0 && (
                <span style={{
                  padding: '2px 8px', borderRadius: 10, background: '#333', color: '#eee',
                  fontSize: '0.72rem',
                }}>Level {q.levelReq}+</span>
              )}
              <code style={{ color: '#888', fontSize: '0.75rem' }}>{q.varPath}</code>
            </div>
            <h2 style={{ margin: '4px 0 0', color: '#f5d38a' }}>{q.name}</h2>
          </div>
        </div>

        <p style={{ margin: '10px 0', color: '#ccc', fontStyle: 'italic' }}>{q.description || '—'}</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6, marginBottom: 12 }}>
          {chip('Given by', q.giver)}
          {chip('NPC id', npcIdLabel)}
          {chip('Location', q.location)}
          {chip('Destination', destinationLabel)}
          {chip('Completed when', q.completedWhen.length ? q.completedWhen.join(', ') : '—')}
          {chip('Failed when', q.failedWhen.length ? q.failedWhen.join(', ') : '—')}
        </div>

        {q.stages && q.stages.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '0.88rem', color: '#ccc' }}>Progress Stages</h4>
            <table className="admin-table" style={{ fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>State value</th>
                  <th>Milestone</th>
                </tr>
              </thead>
              <tbody>
                {q.stages.map((st, i) => {
                  const range = (st.max !== undefined && st.max !== st.min) ? `${st.min}–${st.max}` : `${st.min}`;
                  return (
                    <tr key={i}>
                      <td><code>{range}</code></td>
                      <td>{st.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {q.acDetail && (
          <div style={{ marginTop: 14, padding: 10, background: 'rgba(240,128,192,0.06)', border: '1px solid rgba(240,128,192,0.25)', borderRadius: 4 }}>
            <h4 style={{ margin: '0 0 6px', color: '#f080c0' }}>Advanced Class Trial — {q.acDetail.acName}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6, marginBottom: 8 }}>
              {chip('Mentor', q.acDetail.mentorName)}
              {chip('Mentor area', q.acDetail.mentorArea)}
              {chip('Mentor position', q.acDetail.mentorPos ? `(${q.acDetail.mentorPos.x}, ${q.acDetail.mentorPos.y})` : '—')}
              {chip('Task', q.acDetail.killTarget
                ? `Kill ${q.acDetail.killTarget.count} × ${q.acDetail.killTarget.type}`
                : (q.acDetail.fetchItem ? `Fetch ${q.acDetail.fetchItem}` : 'Action-based (see task text)'))}
              {chip('Boss fight', q.acDetail.fightMonster
                ? `${q.acDetail.fightMonster.name} (Lv ${q.acDetail.fightMonster.level}, ${q.acDetail.fightMonster.hp} HP)`
                : '—')}
              {chip('Reward gold', q.acDetail.reward.gold)}
            </div>
            {q.acDetail.taskDesc && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase' }}>Task brief</div>
                <div style={{ color: '#ddd', fontSize: '0.82rem' }} dangerouslySetInnerHTML={{ __html: q.acDetail.taskDesc }} />
              </div>
            )}
            {q.acDetail.reward.itemStr && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase' }}>Reward item</div>
                <code style={{ color: '#ddd', fontSize: '0.78rem' }}>{q.acDetail.reward.itemStr}</code>
              </div>
            )}
            {q.acDetail.reward.seedXp && (
              <div>
                <div style={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase' }}>Reward seed XP</div>
                <code style={{ color: '#ddd', fontSize: '0.78rem' }}>
                  {Object.entries(q.acDetail.reward.seedXp).map(([k, v]) => `${k}: ${v}`).join(', ')}
                </code>
              </div>
            )}
          </div>
        )}

        {(q.prev || q.next) && (
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              {q.prev ? (
                <button
                  className="admin-btn admin-btn-sm"
                  onClick={() => setSelectedQuestPath(q.prev.varPath)}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  ← Prev: {q.prev.name}
                </button>
              ) : (
                <div className="admin-muted" style={{ fontSize: '0.8rem' }}>⦵ Chain start</div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              {q.next ? (
                <button
                  className="admin-btn admin-btn-sm"
                  onClick={() => setSelectedQuestPath(q.next.varPath)}
                  style={{ width: '100%', textAlign: 'right' }}
                >
                  Next: {q.next.name} →
                </button>
              ) : (
                <div className="admin-muted" style={{ fontSize: '0.8rem', textAlign: 'right' }}>Chain end ⦵</div>
              )}
            </div>
          </div>
        )}

        {renderQuestTopology(q)}
      </div>
    );
  };

  // Render the topology (outcomes + cross-chain edges) for a quest, using
  // the branching data attached by the server's topology parser. Quiet
  // no-op when the quest has no parsed outcomes (AC trials, side quests
  // with no .dat hub representation, etc.).
  const renderQuestTopology = (q) => {
    const outcomes = Array.isArray(q.outcomes) ? q.outcomes : [];
    const outbound = Array.isArray(q.outboundEdges) ? q.outboundEdges : [];
    const inbound = Array.isArray(q.inboundEdges) ? q.inboundEdges : [];
    const aligns = Array.isArray(q.alignmentShifts) ? q.alignmentShifts : [];
    if (!outcomes.length && !outbound.length && !inbound.length) return null;

    const ROLE_COLOR = {
      offer: '#9aa0a6',
      accept: '#7cd67c',
      decline: '#c0a060',
      remind: '#9aa0a6',
      fail: '#e07070',
      complete: '#7cd67c',
      reward: '#f0c060',
      'fight-win': '#7cd67c',
      'fight-lose': '#e07070',
      'fight-flee': '#c0a060',
      'fight-capture': '#88c0ff',
      'check-pass': '#7cd67c',
      'check-fail': '#e07070',
      'check-fail-win': '#c0a060',
      choice: '#88c0ff',
      step: '#9aa0a6',
      dialog: '#6a6a6a',
    };

    const WRITE_TAG_STYLE = {
      lockout:              { bg: '#3a1414', color: '#ff8888', border: '#6a2a2a' },
      'cross-chain':        { bg: '#14223a', color: '#88bbff', border: '#2a3a6a' },
      open:                 { bg: '#143a22', color: '#88ff88', border: '#2a6a3a' },
      accept:               { bg: '#14223a', color: '#88bbff', border: '#2a3a6a' },
      complete:             { bg: '#143a14', color: '#8fe08f', border: '#2a6a2a' },
      'completion-variant': { bg: '#143a22', color: '#9fd4a0', border: '#2a6a3a' },
      decline:              { bg: '#3a2e14', color: '#f0c060', border: '#6a5a2a' },
      fail:                 { bg: '#3a1414', color: '#e07070', border: '#6a2a2a' },
      checkpoint:           { bg: '#1a1a2a', color: '#aaaaaa', border: '#333' },
      align:                { bg: '#2a1a2a', color: '#f0a0c0', border: '#6a3a5a' },
      delta:                { bg: '#1a1a1a', color: '#aaaaaa', border: '#333' },
    };

    const writeBadge = (w, i) => {
      const primaryTag =
        (w.tags || []).find((t) => WRITE_TAG_STYLE[t]) || 'delta';
      const style = WRITE_TAG_STYLE[primaryTag] || WRITE_TAG_STYLE.delta;
      const label = `${w.chain}.${w.key} ${w.op || '='} ${w.value}`;
      const title = (w.tags || []).join(', ') || 'state write';
      return (
        <span
          key={i}
          title={title}
          style={{
            display: 'inline-block',
            padding: '2px 6px',
            margin: '2px 4px 2px 0',
            fontSize: '0.72rem',
            fontFamily: 'monospace',
            background: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            borderRadius: 3,
          }}
        >
          {label}
        </span>
      );
    };

    const edgePill = (e, dir) => (
      <button
        key={`${dir}-${e.from}-${e.to}-${e.outcomeLabel}`}
        onClick={() => {
          const target = dir === 'out' ? e.to : e.from;
          setSelectedQuestPath(target);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 8px',
          margin: '3px 6px 3px 0',
          background: e.lockoutCurrent ? '#3a1414' : '#14223a',
          border: `1px solid ${e.lockoutCurrent ? '#6a2a2a' : '#2a3a6a'}`,
          borderRadius: 4,
          color: e.lockoutCurrent ? '#ff8888' : '#88bbff',
          fontSize: '0.78rem',
          cursor: 'pointer',
        }}
        title={`via ${e.outcomeLabel}${e.lockoutCurrent ? ' (current chain locked out)' : ''}`}
      >
        {dir === 'out' ? '→ ' : '← '}
        <strong>
          {dir === 'out' ? (e.toName || e.to) : (e.fromName || e.from)}
        </strong>
        <span style={{ opacity: 0.7, fontFamily: 'monospace', fontSize: '0.72rem' }}>
          {e.outcomeLabel}
        </span>
        {e.lockoutCurrent && <span style={{ fontSize: '0.7rem' }}>🔒</span>}
      </button>
    );

    return (
      <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #333' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '0.88rem', color: '#ccc' }}>
          Branching Outcomes
          <span className="admin-muted" style={{ marginLeft: 8, fontSize: '0.75rem' }}>
            ({outcomes.length} parsed from .dat)
          </span>
        </h4>

        {outbound.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: 2 }}>
              Cross-chain routes OUT of this quest:
            </div>
            {outbound.map((e) => edgePill(e, 'out'))}
          </div>
        )}

        {inbound.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: 2 }}>
              Cross-chain routes INTO this quest:
            </div>
            {inbound.map((e) => edgePill(e, 'in'))}
          </div>
        )}

        {outcomes.length > 0 && (
          <table className="admin-table" style={{ fontSize: '0.8rem', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={{ width: 160 }}>Outcome node</th>
                <th style={{ width: 110 }}>Role</th>
                <th>Writes</th>
              </tr>
            </thead>
            <tbody>
              {outcomes.map((o) => (
                <tr key={o.label}>
                  <td>
                    <code style={{ fontSize: '0.76rem' }}>{o.label}</code>
                    {(o.hasFight || o.hasCheck) && (
                      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 2 }}>
                        {o.hasFight && '⚔ fight '}
                        {o.hasCheck && '🎯 skill check '}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{
                      padding: '1px 6px',
                      borderRadius: 3,
                      background: '#222',
                      border: `1px solid ${(ROLE_COLOR[o.role] || '#555')}55`,
                      color: ROLE_COLOR[o.role] || '#ccc',
                      fontSize: '0.72rem',
                    }}>{o.role}</span>
                  </td>
                  <td>
                    {(o.writes || []).length === 0 ? (
                      <span className="admin-muted" style={{ fontSize: '0.76rem' }}>—</span>
                    ) : (
                      (o.writes || []).map(writeBadge)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {aligns.length > 0 && (
          <div style={{ marginTop: 8, fontSize: '0.76rem', color: '#f0a0c0' }}>
            <strong>Alignment shifts:</strong>{' '}
            {aligns.map((a) => `${a.outcomeLabel}: ${a.axis} ${a.op} ${a.value}`).join(' · ')}
          </div>
        )}
      </div>
    );
  };

  const renderBugReports = () => {
    // Export text overlay
    if (bugExportText) {
      return (
        <div className="admin-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3>📋 Exported Report</h3>
            <div>
              <button className="admin-btn" onClick={() => {
                navigator.clipboard.writeText(bugExportText);
                logAction('Export text copied to clipboard');
              }}>📋 Copy to Clipboard</button>
              <button className="admin-btn" style={{ marginLeft: 8 }} onClick={() => setBugExportText(null)}>← Back</button>
            </div>
          </div>
          <pre style={{
            background: '#0d1117', border: '1px solid #333', borderRadius: 6,
            padding: 16, color: '#e0e0e0', fontSize: '0.85rem', whiteSpace: 'pre-wrap',
            wordBreak: 'break-word', maxHeight: '70vh', overflowY: 'auto', fontFamily: 'monospace'
          }}>{bugExportText}</pre>
        </div>
      );
    }

    // Detail view
    if (selectedBug) {
      const b = selectedBug;
      const gs = typeof b.game_state === 'string' ? JSON.parse(b.game_state) : b.game_state;
      const actions = typeof b.recent_actions === 'string' ? JSON.parse(b.recent_actions) : b.recent_actions;
      const errData = typeof b.error_data === 'string' ? JSON.parse(b.error_data) : b.error_data;
      const char = gs?.character;

      return (
        <div>
          <button className="admin-btn" onClick={() => setSelectedBug(null)} style={{ marginBottom: 12 }}>← Back to List</button>
          <div className="admin-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Bug #{b.id} — {b.report_type === 'manual' ? '🐛 Player Report' : '⚡ Auto Error'}</h3>
              <span style={{ color: b.resolved ? '#4ade80' : '#ef4444', fontWeight: 'bold' }}>
                {b.resolved ? '✅ Resolved' : '🔴 Open'}
              </span>
            </div>
            <div style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: 12 }}>
              Reporter: <strong style={{ color: '#e0e0e0' }}>{b.character_name || 'Unknown'}</strong> | 
              Date: {new Date(b.created_at).toLocaleString()}
              {b.client_url && <> | URL: {b.client_url}</>}
            </div>

            {b.description && (
              <div className="admin-panel" style={{ background: '#1a1a2e', marginBottom: 12 }}>
                <h4 style={{ color: '#f0c060', margin: '0 0 8px 0' }}>Player Description</h4>
                <p style={{ color: '#e0e0e0', whiteSpace: 'pre-wrap', margin: 0 }}>{b.description}</p>
              </div>
            )}

            {errData && (
              <div className="admin-panel" style={{ background: '#2a1010', marginBottom: 12 }}>
                <h4 style={{ color: '#ff8888', margin: '0 0 8px 0' }}>Error Data</h4>
                <p style={{ color: '#ff8888', margin: '0 0 4px 0' }}><strong>Message:</strong> {errData.message}</p>
                {errData.source && <p style={{ color: '#aaa', margin: '0 0 4px 0' }}><strong>Source:</strong> {errData.source}:{errData.line}:{errData.column}</p>}
                {errData.stack && <pre style={{ color: '#ccc', fontSize: '0.8rem', whiteSpace: 'pre-wrap', background: '#111', padding: 8, borderRadius: 4 }}>{errData.stack}</pre>}
                {errData.componentStack && <pre style={{ color: '#ccc', fontSize: '0.8rem', whiteSpace: 'pre-wrap', background: '#111', padding: 8, borderRadius: 4, marginTop: 4 }}>{errData.componentStack}</pre>}
              </div>
            )}

            {actions && actions.length > 0 && (
              <div className="admin-panel" style={{ background: '#0a1a2a', marginBottom: 12 }}>
                <h4 style={{ color: '#88bbff', margin: '0 0 8px 0' }}>Recent Actions ({actions.length})</h4>
                <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                  {actions.map((a, i) => (
                    <div key={i} style={{ color: '#bbb', padding: '2px 0' }}>
                      <span style={{ color: '#666' }}>[{a.timestamp ? new Date(a.timestamp).toLocaleTimeString() : '?'}]</span>{' '}
                      <span style={{ color: '#88bbff' }}>{a.type}</span>: {a.detail}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {char && (
              <div className="admin-panel" style={{ background: '#1a1a10', marginBottom: 12 }}>
                <h4 style={{ color: '#c9a84c', margin: '0 0 8px 0' }}>Character State</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: '0.85rem', color: '#ccc' }}>
                  <div>Name: <strong>{char.name}</strong></div>
                  <div>Level: {char.level}</div>
                  <div>Class: {char.class}</div>
                  <div>Race: {char.race}</div>
                  <div>HP: {(char.max_hp || 0) - (char.damage || 0)}/{char.max_hp}</div>
                  <div>MP: {(char.max_mp || 0) - (char.used_mp || 0)}/{char.max_mp}</div>
                  <div>Area: {char.current_area}</div>
                  <div>Pos: ({char.x_position}, {char.y_position})</div>
                  <div>Battle: {char.battle_state || 'none'}</div>
                  <div>Gold: {char.gold}</div>
                </div>
                {char.equipment && (
                  <details style={{ marginTop: 8, color: '#aaa', fontSize: '0.8rem' }}>
                    <summary>Equipment</summary>
                    <pre style={{ margin: 4, whiteSpace: 'pre-wrap' }}>{JSON.stringify(char.equipment, null, 2)}</pre>
                  </details>
                )}
                {char.inventory && (
                  <details style={{ marginTop: 4, color: '#aaa', fontSize: '0.8rem' }}>
                    <summary>Inventory ({Array.isArray(char.inventory) ? char.inventory.length : '?'} items)</summary>
                    <pre style={{ margin: 4, whiteSpace: 'pre-wrap' }}>{JSON.stringify(char.inventory, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}

            {/* Admin actions */}
            <div className="admin-panel" style={{ background: '#1a1a2e' }}>
              <h4 style={{ color: '#f0c060', margin: '0 0 8px 0' }}>Admin Actions</h4>
              <textarea
                value={bugAdminNotes}
                onChange={e => setBugAdminNotes(e.target.value)}
                placeholder="Admin notes..."
                style={{ width: '100%', background: '#0d1117', color: '#e0e0e0', border: '1px solid #444', borderRadius: 4, padding: 8, minHeight: 60, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button className="admin-btn" onClick={() => handleBugSaveNotes(b.id)}>💾 Save Notes</button>
                <button className="admin-btn admin-btn-success" onClick={() => handleBugResolve(b.id, !b.resolved)}>
                  {b.resolved ? '🔄 Reopen' : '✅ Resolve'}
                </button>
                <button className="admin-btn" onClick={() => handleBugExport(b.id)}>📤 Export for AI</button>
                <button className="admin-btn admin-btn-danger" onClick={() => setConfirmAction({
                  message: `Delete bug report #${b.id}? This cannot be undone.`,
                  onConfirm: () => handleBugDelete(b.id)
                })}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // List view
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>🐛 Bug Reports ({bugTotal})</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={bugFilter} onChange={e => { setBugFilter(e.target.value); setBugPage(0); }}
              className="admin-input" style={{ width: 'auto' }}>
              <option value="unresolved">Open</option>
              <option value="resolved">Resolved</option>
              <option value="manual">Player Reports</option>
              <option value="auto">Auto Errors</option>
              <option value="">All</option>
            </select>
            <button className="admin-btn" onClick={handleBugBatchExport}>📤 Export All Open for AI</button>
          </div>
        </div>

        {bugReports.length === 0 ? (
          <div className="admin-panel"><p className="admin-muted">No bug reports found.</p></div>
        ) : (
          <table className="admin-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Reporter</th>
                <th>Description / Error</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bugReports.map(r => {
                const errData = typeof r.error_data === 'string' ? JSON.parse(r.error_data) : r.error_data;
                const summary = r.description
                  ? r.description.substring(0, 100) + (r.description.length > 100 ? '...' : '')
                  : errData?.message?.substring(0, 100) || '(no description)';
                return (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => handleBugViewDetail(r.id)}>
                    <td>{r.id}</td>
                    <td>{r.report_type === 'manual' ? '🐛' : '⚡'}</td>
                    <td>{r.character_name || '?'}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>{r.resolved ? '✅' : '🔴'}</td>
                    <td>
                      <button className="admin-btn admin-btn-sm" onClick={e => { e.stopPropagation(); handleBugResolve(r.id, !r.resolved); }}>
                        {r.resolved ? 'Reopen' : 'Resolve'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {bugTotal > 50 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12 }}>
            <button className="admin-btn" disabled={bugPage === 0} onClick={() => setBugPage(p => p - 1)}>← Previous</button>
            <span style={{ color: '#aaa', lineHeight: '32px' }}>Page {bugPage + 1} of {Math.ceil(bugTotal / 50)}</span>
            <button className="admin-btn" disabled={(bugPage + 1) * 50 >= bugTotal} onClick={() => setBugPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  // Client-side admin guard — redirect non-admins to game
  if (!user || !user.isAdmin) {
    return <Navigate to="/game" replace />;
  }

  return (
    <div className="admin-container">
      <ConfirmDialog />

      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>Loria Admin Panel</h1>
          <span className="admin-user">Logged in as {user?.username || 'Admin'}</span>
        </div>
        <div className="admin-header-right">
          <button className="admin-btn admin-btn-back" onClick={() => navigate('/game')}>
            ⬅️ Back to Game
          </button>
          <button className="admin-btn admin-btn-danger" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="admin-tabs">
        {[
          { id: 'dashboard', label: '✨ Dashboard', },
          { id: 'accounts', label: '👥 Accounts' },
          { id: 'characters', label: '⚔️ Characters' },
          { id: 'monsters', label: '✨ Monsters' },
          { id: 'items', label: '✨ Items' },
          { id: 'moderation', label: '🛡️ Moderation' },
          { id: 'reports', label: '🚩 Reports' },
          { id: 'audit', label: '📜 Audit' },
          { id: 'crafting', label: '⚒️ Crafting' },
          { id: 'bugs', label: '🐛 Bug Reports' },
          { id: 'questguide', label: '📖 Quest Guide' },
          { id: 'lore', label: '📜 Lore' },
          { id: 'tools', label: '✨ Tools' },
          { id: 'worldbuilder', label: '✨ World Builder' },
        ].map(tab => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="admin-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'accounts' && renderAccounts()}
        {activeTab === 'characters' && renderPlayers()}
        {activeTab === 'monsters' && renderMonsters()}
        {activeTab === 'items' && renderItems()}
        {activeTab === 'moderation' && renderModeration()}
        {activeTab === 'reports' && renderPlayerReports()}
        {activeTab === 'audit' && renderAuditLog()}
        {activeTab === 'crafting' && renderCrafting()}
        {activeTab === 'bugs' && renderBugReports()}
        {activeTab === 'questguide' && renderQuestGuide()}
        {activeTab === 'lore' && renderLore()}
        {activeTab === 'tools' && renderTools()}
        {activeTab === 'worldbuilder' && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}>
            <WorldBuilder onBack={() => setActiveTab('dashboard')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
